import { registerTranslations } from "../utils/i18n.ts";
import { COMMS_EN, COMMS_DE } from "./commsContent.ts";
import { COMMS_QUIP_EN, COMMS_QUIP_DE } from "./commanderQuips.ts";
import { COMMS_BRIEF_EN, COMMS_BRIEF_DE } from "./PreRunScreenData.ts";
import { STORY_EN, STORY_DE } from "./storyContent.ts";

/** Register all UI translations for en and de. */
export function registerUITranslations(): void {
  registerTranslations("en", {
    "hud.credits": "¢ {{value}}",
    // S146/BL-140 — the left-HUD cell label over the gold ¢ read.
    "hud.creditsLabel": "CREDITS",
    // S146/BL-140 — a11y group name for the left HUD (Credits/Integrity/Wave).
    "hud.statusLabel": "MAINFRAME STATUS",
    "hud.lives": "♥ {{value}}",
    "hud.integrity": "INTEGRITY",
    "a11y.coreHit": "Mainframe hit — integrity {{value}}",
    "hud.wave": "WAVE {{value}}",
    "hud.waveMax": "/ {{value}}",
    // BL-121 — the persistent, glanceable act marker (which act am I in?)
    // D-335 correction (docs/decisions.md, amended) — the owner asked for
    // the WORD ("ACT I"), not a bare numeral; a real-Chromium sweep showed
    // the WORD form ("ACT I"/"ACT II"/"ACT III", built from this prefix + the
    // bare numeral below) overflows the WAVE row somewhere in the shipped
    // scale/wave/rail range, so HUD.ts's `refreshActBadge` renders it live
    // and falls back to the bare numeral only on the device/configuration
    // where it genuinely doesn't fit (never a static always-numeral string).
    "hud.actPrefix": "ACT",
    "hud.act.1": "I",
    "hud.act.2": "II",
    "hud.act.3": "III",
    "hud.score": "SCORE {{value}}",
    "hud.endlessFrozen": "SCORE FROZEN — ENDLESS",
    // BL-63 — the score-race + the multiplier transparency.
    "hud.mult": "MULT",
    "hud.multIdle": "no chain",
    "hud.mult.tip.name": "SCORE MULTIPLIER",
    "hud.mult.tip.impact": "Chain kills to raise it",
    "hud.mult.tip.how1": "Each kill opens a short window — keep killing before the bar drains to climb the tiers.",
    "hud.mult.tip.how2": "A gap (the bar empties) resets the chain to ×1.",
    "hud.mult.tip.how3": "Higher chain → bigger score multiplier.",
    "hud.chain": "{{value}}-KILL CHAIN",
    "hud.multWindow": "+WINDOW",
    "hud.multBoost": "+MULT",
    "hud.worldLead": "⚑ WORLD LEAD",
    "hud.youAreNumber1": "you ARE #1",
    "hud.you": "YOU",
    "hud.vsRank": "vs #1 · {{holder}} {{value}}",
    "hud.vsPersonal": "vs PB · {{value}}",
    "hud.rank1": "#1",
    // BL-109 — text-size (a11y) control labels. The S/M/L glyphs are literal
    // (universal type-scale marks); these are the group + per-option a11y names.
    "gameover.title": "SYSTEM COMPROMISED",
    "gameover.subtitle": "MAINFRAME INTEGRITY: ZERO",
    "gameover.score": "FINAL SCORE: {{value}}",
    "gameover.campaignResult": "↑ CAMPAIGN COMPLETE — WAVE {{value}} RESULT",
    "score.bonus.label": "INTEGRITY BONUS",
    "score.bonus.value": "+{{value}}",
    "score.logged": "↑ LOGGED TO THE GLOBAL DEFENSE LOG",
    "unlock.newAi": "NEW AI DECRYPTED",
    "unlock.deployable": "NOW DEPLOYABLE",
    "gameover.restart": "RESTART RUN",
    "gameover.mainMenu": "MAIN MENU",
    "gameover.stat.wave": "HIGHEST WAVE",
    "gameover.stat.kills": "THREATS NEUTRALIZED",
    "gameover.stat.towers": "NODES DEPLOYED",
    "gameover.stat.gold": "CREDITS EARNED",
    "gameover.stat.echelon": "ECHELON SIGNATURE",
    "gameover.stat.echelonResident": "RESIDENT",
    "gameover.carrier": "CARRIER STILL OPEN — SHE REMAINS.",
    "gameover.onRecord": "WAVE {{value}} — ON THE RECORD.",
    "gameover.comicAlt":
      "Comic panel: magenta circuitry overruns the mainframe hall and ECHELON's vast circuit-ring eye gazes down — the core flickers, the screen is hers.",
    // S130 — the act-door transition cards (Story Bible §6.4)
    "act.card.2.title": "ACT II — HER SIGNAL",
    "act.card.2.sub": "TRACE ANOMALY — A SECOND SIGNATURE RIDES THE STREAM",
    "act.card.3.title": "ACT III — DOMINION",
    "act.card.3.sub": "GRID AUTHORITY COMPROMISED — HOLD THE LINE",
    "victory.title": "MAINFRAME SECURED",
    "victory.subtitle": "INTRUSION REPELLED",
    "victory.stat.wave": "WAVE CLEARED",
    "victory.stat.score": "FINAL SCORE",
    "victory.stat.echelon": "ECHELON SIGNATURE",
    "victory.stat.echelonPurged": "PURGED",
    "victory.exit": "EXIT",
    "victory.restart": "RESTART",
    "victory.continue": "ECHO MODE (ENDLESS)",
    "victory.epilogue": "THE LOG RECORDS: YOU HELD.",
    "victory.echoHint": "HER ECHO PERSISTS IN THE WIRES.",
    "victory.comicAlt":
      "Comic panel: the mainframe core shines green again as the last magenta corruption dissolves — ECHELON's half-formed eye breaks apart above the hall.",
    "wave.indicator": "▶ START WAVE {{value}}",
    "wave.build.phase": "BUILD PHASE",
    "wave.build.hint": "DEPLOY YOUR DEFENSES",
    "coach.tapTower": "TAP A TOWER",
    "coach.tapPlace": "TAP TO PLACE",
    "draft.banner": "// INTERCEPTED SKILL — SELECT TO INSTALL",
    "draft.skip": "DISCARD (+50¢)",
    "draft.installed": "▸ INSTALLED",
    "payload.header": "// PAYLOAD",
    "payload.armed": "ARMED",
    "payload.deploy": "DEPLOY",
    "payload.timewarp": "TIME WARP",
    "payload.rackFull": "PAYLOAD RACK FULL — deploy one first",
    "payload.how.global": "Deploys instantly — fires automatically (no aim).",
    "payload.how.targetEnemy": "◎ AIM — click an enemy to deploy.",
    "payload.how.targetCell": "◎ AIM — click a grid cell to deploy.",
    "payload.how.timewarp": "Deploys instantly — freezes all enemies (no aim).",
    "payload.timewarp.impact": "Freeze ALL enemies for a few seconds",
    "payload.armed.banner": "{{name}} ARMED — click to deploy",
    "skill.deck.empty": "EMPTY",
    "skill.overview.open": "SKILLS [S]",
    "audio.music": "MUSIC",
    "audio.sound": "SOUND",
    "audio.volume": "VOLUME",
    "audio.audio": "AUDIO",
    "audio.mute": "Mute all audio",
    "audio.unmute": "Unmute all audio",
    "hud.pause": "⏸ PAUSE",
    "hud.resume": "▶ RESUME",
    "hud.speed.1x": "1×",
    "hud.speed.2x": ">> 2×",
    // BL-63 — the ⚙ gear (was: the dropdown's re-housed controls; since spec
    // 2026-pause-settings-overlays Task-003 the gear opens the SETTINGS MODAL
    // and `hud.audio.section` / `hud.muteAll` / `hud.library` are read there).
    "hud.gear": "OPTIONS",
    // 2026-08-11 — the in-run HUD control opens the PAUSE modal (settings is
    // reached from the ⚙ inside it), so it needs its own label. `hud.gear`
    // stays: the Boot and Pre-Run gears still open the options dropdown.
    "hud.pauseMenu": "PAUSE MENU",
    "hud.audio.section": "// AUDIO",
    "hud.muteAll": "MUTE ALL",
    "hud.restartRun": "↻ RESTART RUN",
    "hud.exitToMenu": "⏻ EXIT TO MENU",
    // Task-012 (AC-16, O-11b) — the gear row that opens the in-game Enemy
    // Library overlay (option B, `phase2-options.md` §3).
    "hud.library": "◈ THREAT ARCHIVE",
    "library.title": "THREAT ARCHIVE",
    "library.count": "{{known}}/{{total}} KNOWN",
    "library.close": "✕ CLOSE",
    "library.unknown": "SIGNAL UNKNOWN",
    "library.encrypted": "ENCRYPTED",
    "library.opened": "Threat archive opened.",
    // D-340 — the empty dossier states its own emptiness instead of collapsing
    // to a void. The console's voice: no data yet, and how you get it.
    "library.redacted.title": "NO DOSSIER",
    "library.redacted.body":
      "Nothing has been recorded on this signal. Dossiers are written on first contact — hold the line and the archive fills itself.",
    // BL-115 — the runtime locale switch (the EN/DE toggle's label).
    "setting.language": "LANGUAGE",
    // BL-130 — the PreRun gear dropdown: the settings section header + the
    // (now-i18n'd) RESET PROGRESS control folded into the gear.
    "setting.section": "// SETTINGS",
    "prerun.reset": "⟲ RESET PROGRESS",
    "prerun.resetConfirm": "⚠ CONFIRM WIPE?",
    // Spec 2026-pause-settings-overlays (AC-3) — the pause MODAL splits the old
    // one-line `pause.message` into an icon + a title + a capitalised subtitle.
    // `pause.message` itself is RETIRED (Task-003): its last reader was the HUD
    // gear dropdown's section header (`HUD.ts:1177` @ d888033 — the line is a
    // historical citation, the call site no longer exists), and the dropdown
    // is gone with it.
    "pause.title": "PAUSED",
    "pause.subtitle": "Click or press ESC to resume",
    // 2026-08-12 — the touch reading of the line above. Neither half of
    // "Click or press ESC" is true on an iPad; CSS picks one on pointer:coarse.
    "pause.subtitleTouch": "Tap to resume",
    // Spec 2026-pause-settings-overlays (AC-4) — the settings MODAL. `// AUDIO`
    // (hud.audio.section), `MUTE ALL` (hud.muteAll) and `◈ THREAT ARCHIVE`
    // (hud.library) are the dropdown's own shipped strings, re-housed verbatim;
    // only these five are new. `// CONTROLS` (hud.controls.section) retired with
    // the dropdown — RESTART and EXIT live on the pause modal now.
    "settings.title": "SETTINGS",
    "settings.subtitle": "Audio & game options",
    "settings.game.section": "// GAME",
    "settings.apply": "✔ APPLY",
    "settings.back": "BACK",
    "skilldeck.header": "// LOADOUT",
    // D-333 — the header's trailing open-overview glyph (retired the
    // standalone "SKILLS [S]" row; the header itself is now the tap target).
    "skilldeck.headerHint": "[S]",
    "spawn.icon": "⚠",
    "prerun.operator": "OPERATOR",
    "prerun.modules": "LOADOUT MODULES",
    "prerun.selectModules": "SELECT 3 MODULES",
    "prerun.moduleSlot": "SLOT {{n}}",
    "prerun.modulesSelected": "MODULES SELECTED",
    "prerun.modcat.economy": "ECONOMY",
    "prerun.modcat.combat": "COMBAT",
    "prerun.modcat.analytics": "ANALYTICS",
    "prerun.modcat.defense": "DEFENSE",
    "prerun.modcat.adaptive": "ADAPTIVE",
    "prerun.modcat.system": "SYSTEM",
    "prerun.locked": "LOCKED",
    // S188/BL-209 — the loadout is 3 slots + a summoned module tray (progressive
    // disclosure: the 12 tiles stay hidden until you open the tray).
    "prerun.loadout.add": "ADD MODULE",
    "prerun.loadout.done": "DONE",
    "prerun.loadout.empty": "EMPTY SLOT",
    // REMOVED 2026-08-16 — `prerun.briefing.*` (title / protocol /
    // objectiveLabel / objective). The two-screen delta drops the mission
    // context from the pre-run flow (D-AC-1 lists act 1's four zones and has
    // no mission zone), and the owner confirmed on 2026-08-16 that it is not
    // carried forward. Verified before deletion: no consumer anywhere in src/
    // — static or via a template key — only the four definitions and one
    // parity test referenced them.
    "prerun.deployGrid": "DEPLOY DEFENSE GRID",
    "prerun.deploySub": "INITIALIZE DEFENSE PROTOCOL",
    // 2026-prestart-two-screen-delta — the two acts and act 1's primary
    // action. The act names are the spec's own headings (D-AC-1 / D-AC-2), so
    // the screen and the contract cannot drift apart.
    "prerun.loadout": "LOADOUT",
    "prerun.tapToChange": "TAP TO CHANGE",
    "prerun.act1": "BUILD YOUR OPERATOR",
    "prerun.act2": "FACE THE THREAT",
    "prerun.advance": "CONTINUE",
    "prerun.operatorId": "OPERATOR ID",
    "prerun.enterInitials": "ENTER INITIALS / ID",
    "prerun.tapInitials": "TAP ▲▼ TO SET ID",
    "prerun.enemyAnalysis": "ENEMY ANALYSIS",
    "prerun.enemyAnalysis.livetag": "LIVE · CH-66",
    "comms.signalOverride": "SIGNAL OVERRIDE · UNKNOWN SOURCE",
    // S193 (BL-273): the TV's channel banners + NOW-LIVE role smalls are
    // translatable copy (the signalOverride precedent); LIVE/REC/CH-XX/OP·XXX
    // and the caller NAMES stay broadcast chrome-by-design.
    "comms.chrome.channelVera": "CH-01 · MAINFRAME FEED",
    "comms.chrome.channelFenrir": "CH-66 · HOSTILE INTERCEPT",
    "comms.chrome.roleOperator": "OPERATOR",
    "comms.chrome.roleOvermind": "OVERMIND",
    "comms.chrome.roleSignal": "SIGNAL",
    "prerun.role.sentinel-7": "DEFENSIVE GUARDIAN",
    "prerun.role.warden": "TACTICAL NETRUNNER",
    "prerun.role.ghost": "STEALTH INFILTRATOR",
    "prerun.role.architect": "HEAVY JUGGERNAUT",
    "prerun.role.cipher": "ENIGMATIC CIPHER",
    "prerun.selected": "SELECTED",
    "prerun.prevOp": "Previous operator",
    "prerun.nextOp": "Next operator",
    "prerun.callsign_label": "OPERATOR OF RECORD",
    // BL-200 — the Deploy-button readiness checklist tooltip (hover/focus/touch).
    // Lists the steps still missing before the run can start; each line carries a
    // ✓/✗ status. Reuses prerun.selectModules for the modules step where apt.
    "prerun.deploy.checklist": "DEPLOY CHECKLIST",
    "prerun.deploy.ready": "ALL SYSTEMS READY — DEPLOY.",
    "prerun.deploy.step.initials": "INITIALS",
    "prerun.deploy.step.model": "AI MODEL",
    "prerun.deploy.step.modules": "MODULES",
    "prerun.deploy.need.initials": "ENTER YOUR INITIALS",
    "prerun.deploy.need.modules": "SELECT 3 MODULES ({{n}}/3)",
    // BL-218 — a locked operator may be previewed in the carousel but cannot
    // deploy; the gate names its remaining unlock runs.
    "prerun.deploy.need.operator": "DECRYPT IN {{n}} RUNS",
    "prerun.operatorLocked": "OPERATOR ENCRYPTED",
    // BL-218 — the cool in-world note on a locked operator card (kept beside the
    // padlock): how to bring this AI online.
    "prerun.lock.title": "ENCRYPTED",
    "prerun.lock.note": "Run {{n}} more sorties to decrypt this AI",
    // S129 / D-227 — the legal & privacy surface + the BL-96 submit notice.
    "legal.open": "LEGAL",
    "legal.title": "LEGAL & PRIVACY",
    "legal.close": "CLOSE",
    "prerun.privacyNotice.more": "PRIVACY ▸",
    // BL-202 — the VERA⇄FENRIR personnel block. VERA's role line (her desk) +
    // FENRIR's THREAT DOSSIER (the antagonist brief that fills his box; lore from
    // STORY_BIBLE §2 — the wolf, ECHELON's herald, commander of every hostile process).
    "prerun.vera.desk": "DEFENSE DESK",
    "prerun.fenrir.class": "CLASS",
    "prerun.fenrir.classV": "ECHELON HERALD — THE WOLF",
    "prerun.fenrir.command": "COMMAND",
    "prerun.fenrir.commandV": "ALL HOSTILE PROCESSES",
    "prerun.fenrir.signal": "SIGNAL",
    "prerun.fenrir.signalV": "CH-66 · HOSTILE INTERCEPT",
    // S188 — FENRIR enemy-analysis stat bars (premium threat card).
    // "prerun.fenrir.high" (static "HIGH" literal) removed 2026-08-15 —
    // the coherence fix in the same pass as AC-17; see PreRunScreen.ts's
    // buildEnemyPanel comment. threatLevel stays: it's still the aria-label
    // for the threat-rail listbox (PreRunScreen.ts:478).
    "prerun.fenrir.threatLevel": "THREAT LEVEL",
    "prerun.fenrir.offensive": "OFFENSIVE",
    "prerun.fenrir.defensive": "DEFENSIVE",
    "prerun.fenrir.swarm": "SWARM CAPACITY",
    "prerun.fenrir.tech": "TECH ADAPTATION",
    "prerun.fenrir.intel": "ENEMY INTEL",
    "prerun.step2": "OPERATOR",
    "prerun.step2_hint": "COMPLETE RUNS TO UNLOCK MORE OPERATORS",
    // Operator blurbs (BL-114) — MUST mirror the real OperatorEffects.
    "operator.sentinel-7.desc": "+1 HP  |  +5% RANGE",
    "operator.warden.desc": "+50 CREDITS  |  -1 HP",
    "operator.ghost.desc": "+15% SYNERGY  |  -10% DMG",
    "operator.architect.desc": "85% SELL REFUND  |  +25 CREDITS",
    "operator.cipher.desc": "+8% RANGE  |  +10% SYNERGY",
    "module.overclocked_boot.name": "OVERCLOCKED BOOT",
    "module.overclocked_boot.desc": "+100 starting credits",
    "module.pattern_recognition.name": "PATTERN RECOGNITION",
    "module.pattern_recognition.desc": "Tower range +5%",
    "module.quick_learner.name": "QUICK LEARNER",
    "module.quick_learner.desc": "Score gain +15%",
    "module.network_optimizer.name": "NETWORK OPTIMIZER",
    "module.network_optimizer.desc": "Synergy effects +10%",
    "module.cache_warmth.name": "CACHE WARMTH",
    "module.cache_warmth.desc": "First tower is free",
    "module.failsafe_protocol.name": "FAILSAFE PROTOCOL",
    "module.failsafe_protocol.desc": "Revive once at 0 HP",
    "module.heuristic_bias.name": "HEURISTIC BIAS",
    "module.heuristic_bias.desc": "Draft shows 4 cards",
    "module.defensive_default.name": "DEFENSIVE DEFAULT",
    "module.defensive_default.desc": "+1 starting HP",
    "module.recursive_loops.name": "RECURSIVE LOOPS",
    "module.recursive_loops.desc": "Combo decay -20%",
    "module.bit_packer.name": "BIT PACKER",
    "module.bit_packer.desc": "6 skill slots",
    "module.cold_boot.name": "COLD BOOT",
    "module.cold_boot.desc": "Boss damage -25% first 5 waves",
    "module.adaptive_throttling.name": "ADAPTIVE THROTTLING",
    "module.adaptive_throttling.desc": "Quality reduces at 14ms",
    // Enemy names (BL-113 — EN mirrors enemies.json, the fallback source)
    "enemy.scout": "Data Packet",
    "enemy.scout.short": "PKT",
    "enemy.golem": "Rootkit",
    "enemy.golem.short": "RTK",
    "enemy.swarm": "Nano-Swarm",
    "enemy.swarm.short": "SWM",
    "enemy.shifter": "Polymorphic Worm",
    "enemy.shifter.short": "PLY",
    "enemy.boss": "Core Breaker",
    "enemy.boss.short": "BRK",
    "enemy.final_boss": "AXIOM-77 Core",
    "enemy.final_boss.short": "AX7",
    "enemy.corrupted_node": "Corrupted Node",
    "enemy.corrupted_node.short": "CND",
    "enemy.cipher_breaker": "Cipher Breaker",
    "enemy.cipher_breaker.short": "CPH",
    "enemy.stealth_daemon": "Stealth Daemon",
    "enemy.stealth_daemon.short": "STH",
    "enemy.firewall_breaker": "Firewall Breaker",
    "enemy.firewall_breaker.short": "FWB",
    // BL-152 (S154) — the two new per-act threats (EN mirrors enemies.json).
    "enemy.jammer": "Jammer",
    "enemy.jammer.short": "JAM",
    "enemy.overseer": "Overseer",
    "enemy.overseer.short": "OVS",
    // BL-155 (S158) — Threat Pack 2 (EN mirrors enemies.json).
    "enemy.leech": "Leech",
    "enemy.leech.short": "LCH",
    "enemy.regenerator": "Regenerator",
    "enemy.regenerator.short": "RGN",
    "enemy.splitter": "Splitter",
    "enemy.splitter.short": "SPL",
    "enemy.splitling": "Splinter",
    "enemy.splitling.short": "SPN",
    // Task-009 (Spec B) — the three W20+ data archetypes (EN mirrors enemies.json).
    "enemy.hunter_killer": "Hunter-Killer",
    "enemy.hunter_killer.short": "HNK",
    "enemy.custodian": "Custodian",
    "enemy.custodian.short": "CST",
    "enemy.siphon": "Siphon",
    "enemy.siphon.short": "SPH",
    // BL-161 (S162) — the threat-dossier ROLE tags (enemyCardModel.deriveRole).
    // EN mirrors the canonical fallbacks verbatim (the EN card stays byte-identical).
    "enemy.role.overmind": "FINAL CORE · OVERMIND",
    "enemy.role.apex": "APEX THREAT",
    "enemy.role.spawner": "SPAWNER",
    "enemy.role.shifter": "SHIFTER",
    "enemy.role.suppressor": "SUPPRESSOR",
    "enemy.role.commander": "COMMANDER",
    "enemy.role.siphon": "SIPHON",
    "enemy.role.mender": "MENDER",
    "enemy.role.divider": "DIVIDER",
    "enemy.role.breacher": "BREACHER",
    "enemy.role.heavy": "HEAVY",
    "enemy.role.rusher": "RUSHER",
    "enemy.role.intruder": "INTRUDER",
    // BL-161 (S162) — the dossier ABILITY block (deriveAbility): title + desc per
    // branch. The symbol prefix lives in the title; {{count}}/{{dmg}} stay
    // data-driven (D-256) and interpolate in BOTH locales.
    "enemy.ability.overmind.title": "⟁ OVERMIND",
    "enemy.ability.overmind.desc":
      "Splits into phases at 70% / 30% integrity; each phase spawns a fresh wave of adds. The mainframe's last stand.",
    "enemy.ability.spawn_adds.title": "⊕ SPAWN ADDS",
    "enemy.ability.spawn_adds.desc":
      "Breaches into {{count}}additional threats mid-run — finish it before it multiplies the swarm.",
    "enemy.ability.polymorph.title": "✷ POLYMORPH",
    "enemy.ability.polymorph.desc":
      "Cycles its resistance on an interval — no single element holds against it for long.",
    "enemy.ability.suppression_field.title": "▾ SUPPRESSION FIELD",
    "enemy.ability.suppression_field.desc":
      "Jams nearby towers — slashes their fire rate while it's in range. Spread your towers or take it down first.",
    "enemy.ability.aura_host.title": "❖ AURA HOST",
    "enemy.ability.aura_host.desc":
      "Shields the pack around it — nearby threats take reduced damage. Kill the host and the aura collapses.",
    "enemy.ability.credit_siphon.title": "◎ CREDIT SIPHON",
    "enemy.ability.credit_siphon.desc":
      "Drains your credits for as long as it lives — a hoarder's bane. Kill it fast to stop the bleed.",
    "enemy.ability.self_repair.title": "✚ SELF-REPAIR",
    "enemy.ability.self_repair.desc":
      "Knits its integrity back every second — chip damage won't hold it. Break it with burst.",
    "enemy.ability.fracture.title": "⊟ FRACTURE",
    "enemy.ability.fracture.desc":
      "Killing it splits it into {{count}}fast splinters — bring AoE or it floods the lane.",
    "enemy.ability.firewall_breach.title": "▣ FIREWALL BREACH",
    "enemy.ability.firewall_breach.desc":
      "Tears {{dmg}} off the firewall on contact — a dedicated breacher, intercept it early.",
    "enemy.ability.hardened.title": "◼ HARDENED",
    "enemy.ability.hardened.desc":
      "Heavily armoured — soaks punishment. Concentrate fire to crack the shell.",
    "enemy.ability.rush.title": "» RUSH",
    "enemy.ability.rush.desc":
      "Fast and light — it races the lane. Slow it or intercept early.",
    "enemy.ability.intrusion.title": "· INTRUSION",
    "enemy.ability.intrusion.desc":
      "A standard intrusion packet — the baseline threat on the wire.",
    // Task-002 (2026-threat-archive-console) — FENRIR's cold antagonist intros
    // (enemyCardModel.pickTaunt/revealTaunts). EN mirrors the canonical table
    // verbatim (the EN card stays byte-identical); DE below is a real
    // translation, not a copy (TranslationsParity's must-differ guard covers it).
    "enemy.taunt.new_toy": "Meet my next toy.",
    "enemy.taunt.unseen": "You haven't seen this one.",
    "enemy.taunt.something_new": "Something new for you.",
    "enemy.taunt.analyse_now": "Analyse it while you can.",
    "enemy.taunt.try_stop": "Try to stop this.",
    "enemy.taunt.fresh_nightmare": "A fresh nightmare.",
    // BL-161 (S162) — the static dossier card labels (stat row + matchup row +
    // classification + empty-matchup placeholder). EN mirrors the canonical text.
    "card.label.classification": "⚠ THREAT MANIFEST",
    "card.label.integrity": "INTEGRITY",
    "card.label.velocity": "VELOCITY",
    "card.label.bounty": "BOUNTY",
    "card.label.weakness": "WEAKNESS",
    "card.label.resist": "RESIST",
    "card.label.immune": "IMMUNE",
    "card.label.none": "— none —",
    // BL-172 — the Shield reveal card (EnemyReveal.buildShieldItem), the W15+
    // shield mechanic's epic dossier. EN mirrors the canonical fallbacks verbatim
    // (the EN card stays byte-identical). The {{wave}} (SHIELD_START_WAVE) stays
    // data-driven and interpolates in BOTH locales. The code tokens (CLASS-S,
    // SENTINEL-7, the ◈/⚠/· symbols) stay identical across locales by design.
    "shield.classification": "⚠ DEFENSE PROTOCOL",
    "shield.name": "SHIELD",
    "shield.subtitle": "REINFORCED · HEX-CAGE",
    "shield.stat.protocol.k": "PROTOCOL",
    "shield.stat.protocol.v": "REINFORCED",
    "shield.stat.online.k": "ONLINE",
    "shield.stat.online.v": "WAVE {{wave}}+",
    "shield.ability.title": "◈ HEX-CAGE",
    "shield.ability.desc":
      "A faceted shield absorbs damage before the threat's core is touched. Break the cage with sustained fire, then finish the kill.",
    "shield.footerLeft": "SENTINEL-7 · SHIELD",
    "shield.flavour": "Some of them came back armoured.",
    // Boot Sequence
    "boot.pressKey": "PRESS SPACE TO BOOT",
    // F15-4 — the same prompt for a device with no space bar. Which of the two
    // is shown is a CSS `@media (pointer:coarse)` swap in BootSequenceStyles,
    // not a JS branch, so it follows a paired keyboard/trackpad correctly and
    // survives an orientation or input change with no re-render.
    "boot.tapKey": "TAP TO BOOT",
    "boot.lore1": "AXIOM-77 is awake.",
    "boot.lore2": "The last mainframe stands.",
    "boot.lore3": "You are SENTINEL-7.",
    "boot.signInToCompete": "GLOBAL — SIGN IN TO COMPETE",
    "boot.localBest": "LOCAL BEST: {{value}}",
    "boot.viewGlobalRanks": "VIEW GLOBAL RANKS",
    // Boss system
    "countdown.hint": "PRESS SPACE FOR EARLY START (+{{value}} ¢)",
    "countdown.earlyBonus": "EARLY START +{{value}} ¢",
    "stealth.warning": "⚠ STEALTH UNITS DETECTED — DEPLOY SIGNAL TOWER",
    // S195/BL-282 — the boss-arrival name-slam + the raw-EN canon banners
    // moved onto the i18n seam (S195-F11).
    "boss.arrival.title": "BOSS BREACH",
    "boss.incoming.banner": "⚠ BOSS INCOMING",
    "wave.processClear": "PROCESS CLEAR",
    "warning.coreBreaker": "!! WARNING: CORE BREAKER DETECTED",
    "breach.eruptionImminent": "ERUPTION IMMINENT // [{{col}},{{row}}]",
    "payload.instantFired": "INSTANT FIRED - no slot used",
    // S141 / D-236 — ECHELON mutation telegraphs (Lever 1; her signal mutates
    // the thread). Banner shown at wave-start so the player can recompose.
    "wave.mutation.hardened": "⚠ HER SIGNAL: HARDENED THREAD",
    "wave.mutation.swift": "⚠ HER SIGNAL: SWIFT THREAD",
    "wave.mutation.veiled": "⚠ HER SIGNAL: VEILED THREAD — DEPLOY SIGNAL",
    "wave.mutation.splitting": "⚠ HER SIGNAL: SPLITTING THREAD",
    // BL-152 / BL-153 (S154) — the "NEW THREAT DETECTED" telegraph (once per
    // archetype, on its debut wave). The copy names the threat AND its counter.
    "wave.newthreat.jammer": "⚠ NEW THREAT: JAMMER — IT SUPPRESSES YOUR TOWERS",
    "wave.newthreat.overseer": "⚠ NEW THREAT: OVERSEER — IT SHIELDS NEARBY ENEMIES",
    // BL-155 (S158) — Threat Pack 2 telegraphs (name the threat AND its counter).
    "wave.newthreat.leech": "⚠ NEW THREAT: LEECH — IT DRAINS YOUR CREDITS, KILL IT FAST",
    "wave.newthreat.regenerator": "⚠ NEW THREAT: REGENERATOR — IT HEALS, BURST IT DOWN",
    "wave.newthreat.splitter": "⚠ NEW THREAT: SPLITTER — IT SPLITS ON DEATH, USE AREA DAMAGE",
    "firewall.destroyed": "FIREWALL BREACHED",
    "breach.initiated": "⚠ BREACH PROTOCOL INITIATED",
    "breach.spawnOnline": "SPAWN-2 ONLINE",
    "breach.towerDestroyed":
      "CRITICAL ERROR: Foundation Compromised // Structure Lost",
    "streak.flawless": "★ FLAWLESS STREAK — +50% DRAFT LUCK",
    // BL-241 — combo milestone callouts (×5/×7/×10)
    "combo.milestone.5": "COMBO ×5 — ON FIRE",
    "combo.milestone.7": "COMBO ×7 — DOMINATING",
    "combo.milestone.10": "COMBO ×10 — UNSTOPPABLE",
    // Spawn detection (Signal tower)
    // GA-08 (Spec C+): INTRUSION_SCANNER's payload. The card promised "reveals
    // next 3 wave compositions" and emitted an event with ZERO consumers, so
    // the whole effect never reached the player. `{{n}}` is the wave number,
    // `{{comp}}` the scaled composition line.
    "intel.revealWaves.header": "DEEP SCAN // INCOMING MANIFESTS DECRYPTED",
    "intel.revealWaves.line": "WAVE {{n}} // {{comp}}",
    "intel.revealWaves.none": "DEEP SCAN // NO FURTHER MANIFESTS — FINAL WAVE REACHED",
    "spawn.detected":
      "NEW VECTOR DISCOVERED // NETWORK BREACH ANCHOR LOCALIZED",
    "spawn.forceRevealed": "⚠ CRITICAL: Undetected Breach Active!",
    "signal.overload":
      "DIRECT INTERCEPT // SIGNAL OVERLOAD // NODE EXPOSED & UNIT FRIED",
    // Tower bay labels + tooltip roles (BL-113 — EN mirrors ELEMENT_REGISTRY,
    // the render-time source; towers.json names never render).
    "tower.fire": "MALWARE",
    "tower.fire.role": "DOT / Damage-over-Time",
    "tower.water": "ICE",
    "tower.water.role": "CHAIN / Multi-target",
    "tower.lightning": "EMP",
    "tower.lightning.role": "BURST / Crit",
    "tower.signal": "SIGNAL",
    "tower.signal.role": "SUPPORT / Debuff (no direct damage)",
    "tower.kinetic": "Rail-Sentinel",
    "tower.kinetic.role": "Precision Railgun",
    "tower.virus": "Contagion-Node",
    "tower.virus.role": "Lobbed AoE Infection",
    // BL-213 (S189) — the glanceable one-word ROLE, surfaced at the head of the
    // bay tap/hover tooltip's impact line (NOT a new bay label — the panel is
    // already dense). A new player reads function (DOT/CHAIN/…), not flavour.
    "tower.fire.tag": "DOT",
    "tower.water.tag": "CHAIN",
    "tower.lightning.tag": "BURST",
    "tower.signal.tag": "SUPPORT",
    "tower.kinetic.tag": "RAILGUN",
    "tower.virus.tag": "AOE",
    // TowerSelector i18n (BL-13)
    "tower.header": "// DEPLOY",
    "tower.firewall": "FIREWALL",
    "tower.firewall.role": "BARRIER / Path Blocker",
    "tower.firewall.tag": "BARRIER",
    "tower.firewall.support": "PASSIVE STRUCTURE \u00b7 no direct damage",
    "tower.firewall.special":
      "Blocks enemy pathing. Place strategically to force enemies into longer routes past your towers. Can be destroyed by Firewall Breaker enemies — defend it.",
    "tower.firewall.tip":
      "TIP: Combine with chokepoints to maximize tower coverage. Enemies must path around firewalls.",
    "tower.support": "SUPPORT TOWER \u00b7 no direct damage",
    "tower.cost": "Cost: \u00a2{{cost}}",
    "tower.upgrade.max": "MAX LEVEL",
    "tower.upgrade": "UPGRADE \u00b7 \u00a2{{cost}}",
    "tower.upgrade.insufficient": "UPGRADE \u00b7 \u00a2{{cost}} INSUFFICIENT",
    "tower.sell": "SELL \u00a2{{price}}",
    "tower.sell.label": "SELL",
    "tower.sell.price": "\u00a2{{price}}",
    // BL-297 — the ARMED sell button's own label. Deliberately NOT the full
    // `sell.confirm` ("CONFIRM SELL"), which the standalone dialog still uses:
    // the armed state keeps the button's two-line shape, so the top line is one
    // word and the price stays on the second.
    "tower.sell.confirm": "CONFIRM",
    "tower.next": "NEXT",
    "tower.gold.tease": "signature mechanic at the apex",
    "tower.synergy": "Synergy: {{list}}",
    "tower.stats.support.range": "Range: {{range}}",
    "tower.stat.dmg": "DMG",
    "tower.stat.dps": "DPS",
    "tower.stat.range": "Range",
    "tower.stat.rate": "Rate",
    "tower.stat.eff": "EFF",
    "tower.stat.range.short": "RANGE",
    // ── BL-293 (spec 2026-gameplay-ui-compression) — the mechanic block stops
    //    being a sentence. Each row is KEYWORD + VALUES; every number the old
    //    prose carried survives, the framing around it does not. The old
    //    `tower.special.*` keys are gone with their sentences.
    "tower.mech.dot.k": "BURN",
    "tower.mech.dot.v": "{{dmg}} DMG / {{int}}s \u00b7 {{dur}}s \u00b7 {{total}} total",
    "tower.mech.chain.k": "CHAIN",
    "tower.mech.chain.v": "+{{bounces}} targets \u00b7 80px \u00b7 \u2212{{decay}}% per jump",
    "tower.mech.burst.k": "CRIT",
    "tower.mech.burst.v": "{{chance}}% \u00b7 \u00d7{{mult}} damage",
    "tower.mech.mark.k": "MARK",
    "tower.mech.mark.v": "{{dur}}s \u00b7 +{{bonus}}% damage \u00b7 \u2212{{slow}}% speed",
    "tower.mech.railshot.k": "RAIL",
    "tower.mech.railshot.v": "{{charge}}s charge \u00b7 board-wide",
    "tower.mech.infection.k": "INFECT",
    "tower.mech.infection.v": "{{radius}}px \u00b7 {{tickDmg}}/{{int}}s \u00b7 {{dur}}s",
    "tower.mech.spread.k": "SPREAD",
    "tower.mech.spread.v": "{{chance}}% \u00b7 {{gen}} gen \u00b7 \u2212{{slow}}% speed",
    "tower.mech.pool.k": "POOL",
    "tower.mech.pool.v": "pool {{pool}}s",
    // The capability rows: the star badge carries the NAME, the value says what
    // it does in one clause. No "Lv.N unlocks" prose — the NEXT card owns that.
    // BL-293 — the L3 beat names, lifted out of the sentences that used to
    // hold them so the compressed capability row can carry name and value
    // separately. Extracted from the shipped `.on` strings, never re-typed.
    "tower.cap.fire.l3.name": "SCORCH",
    "tower.cap.water.l3.name": "CRYO-CHAIN",
    "tower.cap.lightning.l3.name": "OVERLOAD",
    "tower.cap.kinetic.l3.name": "ARMOR-PIERCE",
    "tower.cap.signal.l3.name": "HONEYPOT",
    "tower.capv.fire": "splash to nearby foes",
    "tower.capv.water": "bounced foes \u2212{{slow}}% speed",
    "tower.capv.lightning": "guaranteed crit every {{period}}s",
    "tower.capv.kinetic": "ignores armor",
    "tower.capv.signal": "lures marked off their path",
    "tower.capv.virus": "{{spread}}% spread",
    // BL-182 / D-265 (S169) — the per-tower CAPABILITY ARC: each beat is a NAME
    // and one concrete clause, never a sentence. BL-296 retired the
    // `tower.cap.<el>.{locked,on}` prose and the `tower.cap.{next,gold}`
    // sentence templates — the panel composes level · name · clause itself, so
    // a beat now reads identically before and after it unlocks.
    "tower.cap.lv": "L{{level}}",
    "tower.cap.fire.l4.name": "WILDFIRE",
    "tower.cap.fire.l4.desc": "burn jumps to splash hits",
    "tower.cap.fire.l5.name": "FIRESTORM",
    "tower.cap.fire.l5.desc": "wider, hotter splash",
    "tower.cap.water.l4.name": "DEEP-CHILL",
    "tower.cap.water.l4.desc": "+1 chain target",
    "tower.cap.water.l5.name": "FLASH-FREEZE",
    "tower.cap.water.l5.desc": "primary target frozen",
    "tower.cap.lightning.l4.name": "FORK",
    "tower.cap.lightning.l4.desc": "crit forks to a 2nd foe",
    "tower.cap.lightning.l5.name": "CHAIN-STORM",
    "tower.cap.lightning.l5.desc": "crit arcs to all nearby",
    "tower.cap.kinetic.l4.name": "PIERCE CORRIDOR",
    "tower.cap.kinetic.l4.desc": "hits the whole line",
    "tower.cap.kinetic.l5.name": "DOUBLE-SHOT",
    "tower.cap.kinetic.l5.desc": "rail fires twice",
    "tower.cap.virus.l3.name": "VIRULENCE",
    "tower.cap.virus.l3.desc": "spread ramps up",
    // BL-175 / D-266 (S170) — the GOLD pinnacle tier (L6): the "GOLD" badge + the
    // per-element BRAND-NEW signature mechanic (name + one-liner). `tower.cap.gold`
    // frames the next-unlock line as the coveted GOLD upgrade, not a numbered level.
    "tower.gold.badge": "GOLD",
    "tower.cap.fire.gold.name": "HELIOS",
    "tower.cap.fire.gold.desc": "kills detonate in fire",
    "tower.cap.water.gold.name": "ABSOLUTE ZERO",
    "tower.cap.water.gold.desc": "nova freezes a region",
    "tower.cap.lightning.gold.name": "TEMPEST",
    "tower.cap.lightning.gold.desc": "sky-bolts hit the strongest",
    "tower.cap.kinetic.gold.name": "ANNIHILATOR",
    "tower.cap.kinetic.gold.desc": "executes any non-boss",
    "tower.cap.signal.gold.name": "ORACLE",
    "tower.cap.signal.gold.desc": "buffs in range · reveals stealth",
    "tower.cap.virus.gold.name": "PANDEMIC",
    "tower.cap.virus.gold.desc": "infected burst spores on death",
    // Synergies (BL-113 — EN mirrors synergies.json + the resonance pseudo-synergy)
    "synergy.steam": "Steam Burst",
    "synergy.steam.desc": "AoE 8 DMG \u00b7 \u221240 % speed",
    "synergy.plasma": "Plasma Arc",
    "synergy.plasma.desc": "beam \u00d71.5 damage",
    "synergy.electroshock": "Electroshock",
    "synergy.electroshock.desc": "30 % stun \u00b7 0.8s",
    "synergy.nexus": "Elemental Nexus",
    "synergy.nexus.desc": "aura 12 DMG \u00b7 50px",
    "synergy.tracking": "Target Lock",
    "synergy.tracking.desc": "targets marked first",
    "synergy.broadcast": "Broadcast",
    "synergy.broadcast.desc": "mark spreads \u00b7 48px",
    "synergy.burning_mark": "Burning Mark",
    "synergy.burning_mark.desc":
      "fire DOT \u00d72 on marked",
    "synergy.designated": "Designated Marksman",
    "synergy.designated.desc":
      "\u00d70.5 charge, marked",
    "synergy.railarc": "Overcharged Slug",
    "synergy.railarc.desc":
      "kill \u2192 2 \u00b7 80px",
    "synergy.wildfire": "Wildfire",
    "synergy.wildfire.desc":
      "burn 4 \u00b7 +20 % spread",
    "synergy.waterborne": "Waterborne",
    "synergy.waterborne.desc":
      "\u00d72 range \u00b7 +1 gen",
    "synergy.plague": "Plague",
    "synergy.plague.desc":
      "marked: 100 % \u00b7 no cap",
    "synergy.resonance": "RESONANCE",
    "synergy.resonance.desc": "AoE pulse from adjacent tower cluster",
    // Card names (BL-113 — EN mirrors cards.json, the fallback source)
    "card.overload_malware": "OVERLOAD::MALWARE",
    "card.overload_ice": "OVERLOAD::ICE",
    "card.overload_emp": "OVERLOAD::EMP",
    "card.firewall_boost": "PATCH::FORTIFY",
    "card.data_mining": "DATA_MINING.exe",
    "card.overclock": "OVERCLOCK",
    "card.cache_hit": "CACHE_HIT",
    "card.packet_burst": "PACKET_BURST",
    "card.packet_inspector": "PACKET_INSPECTOR",
    "card.piercing_shots": "ARMOR_PIERCE.dll",
    "card.throttle_field": "THROTTLE_FIELD",
    "card.synergy_bridge": "SYNERGY_BRIDGE",
    "card.chain_reaction": "CHAIN_REACTION",
    "card.bounty_protocol": "BOUNTY_PROTOCOL",
    "card.emergency_patch": "EMERGENCY_PATCH",
    "card.intrusion_scanner": "INTRUSION_SCANNER",
    "card.firewall_reinforce": "FIREWALL_REINFORCE",
    "card.thread_priority": "THREAD_PRIORITY",
    "card.greed_protocol": "GREED_PROTOCOL",
    "card.minimalist": "MINIMALIST",
    "card.full_refund": "FULL_REFUND",
    "card.quantum_surge": "QUANTUM_SURGE",
    "card.hyper_threading": "HYPER_THREADING",
    "card.root_access": "ROOT_ACCESS",
    "card.zero_day_exploit": "ZERO_DAY_EXPLOIT",
    "card.memory_overflow": "MEMORY_OVERFLOW",
    "card.glass_protocol": "GLASS_PROTOCOL",
    "card.scorched_earth": "SCORCHED_EARTH",
    "card.resonance": "RESONANCE",
    "card.memory_leak": "MEMORY_LEAK",
    "card.cascade": "CASCADE",
    "card.late_overclock": "THERMAL_RUNAWAY",
    "card.neural_nexus": "NEURAL_NEXUS",
    "card.time_warp": "TIME_WARP",
    "card.recursive_loop": "RECURSIVE_LOOP",
    "card.apex_predator": "APEX_PREDATOR",
    "card.monoculture": "MONOCULTURE",
    "card.kernel_panic": "KERNEL_PANIC",
    "card.pid_hijack": "PID_HIJACK",
    "card.gc_force": "gc --force",
    "card.logic_bomb": "LOGIC_BOMB",
    "card.backdoor": "BACKDOOR",
    "card.fork_bomb": "FORK_BOMB",
    "card.entropy_tax": "ENTROPY_TAX",
    "card.dead_mans_switch": "DEAD_MAN'S_SWITCH",
    // Card descriptions (BL-113 — EN mirrors cards.json, the fallback source)
    "card.overload_malware.desc":
      "Amplifies PULSE heat cores.",
    "card.overload_ice.desc":
      "Supercools SCATTER arrays.",
    "card.overload_emp.desc":
      "stacks with element focus",
    "card.firewall_boost.desc":
      "no slot used",
    "card.data_mining.desc":
      "Siphons +20% bonus credits from every kill.",
    "card.overclock.desc":
      "",
    "card.cache_hit.desc":
      "",
    "card.packet_burst.desc":
      "no slot used",
    "card.packet_inspector.desc":
      "",
    "card.piercing_shots.desc":
      "Devastating with SCATTER arrays.",
    "card.throttle_field.desc":
      "",
    "card.synergy_bridge.desc":
      "Synergies now fire across 8 directions.",
    "card.chain_reaction.desc":
      "Resonance cascade: synergy effects trigger at 1.5× frequency for 3 waves.",
    "card.bounty_protocol.desc":
      "+40% credit yield on kills for 3 waves.",
    "card.emergency_patch.desc":
      "+3 integrity restored immediately.",
    "card.intrusion_scanner.desc":
      "",
    "card.firewall_reinforce.desc":
      "Active firewalls slow adjacent threats by 10% permanently.",
    "card.thread_priority.desc":
      "all nodes, within range",
    "card.greed_protocol.desc":
      "+60% kill credits / −20% global damage.",
    "card.minimalist.desc":
      "+15% global damage per EMPTY skill slot.",
    "card.full_refund.desc":
      "Sold nodes return 100% investment.",
    "card.quantum_surge.desc":
      "",
    "card.hyper_threading.desc":
      "Every node fires at 2× rate for 2 waves.",
    "card.root_access.desc":
      "All enemies throttled to 40% speed for 1 wave.",
    "card.zero_day_exploit.desc":
      "+50% damage vs BOSS and GOLEM class for 2 waves.",
    "card.memory_overflow.desc":
      "Draft phase offers +1 card permanently.",
    "card.glass_protocol.desc":
      "+60% global damage / −2 max integrity.",
    "card.scorched_earth.desc":
      "+30% global damage / kill credits halved.",
    "card.resonance.desc":
      "Each active synergy grants +12% global damage.",
    "card.memory_leak.desc":
      "Strongest enemy class: −40 % HP (non-lethal), then EXPOSED +50 % tower damage.",
    "card.cascade.desc":
      "Each synergy trigger splashes 25 AoE damage to nearby enemies.",
    "card.late_overclock.desc":
      "All nodes permanently fire at +35% rate.",
    "card.neural_nexus.desc":
      "Adjacent 3-element groups fire auto-kill shots every 2s.",
    "card.time_warp.desc":
      "Press [T]: 5s reality freeze — your nodes fire, they don't move.",
    "card.recursive_loop.desc":
      "Every 10th kill detonates shrapnel: 25% of victim max HP to ALL on screen.",
    "card.apex_predator.desc":
      "Your DOMINANT element towers +120% damage / all others −40%.",
    // GA-09 (Spec C+) — CORRECTED. The old copy claimed towers "count as ALL
    // elements for synergy detection", twice. They do not: `monoculture` only
    // gates the code-only RESONANCE cluster synergy. It never touches the
    // same-element pair-skip or the element-matching lookups, so an all-fire
    // board still cannot trigger any of the 12 elemental synergies. The text
    // promised full elemental equivalence and delivered exactly one synergy.
    // Copy corrected to the behaviour, not behaviour to the copy — D-305 stands.
    "card.monoculture.desc":
      "Unlocks RESONANCE on any 3+ adjacent towers, whatever their elements / −25% global damage.",
    "card.kernel_panic.desc":
      "ALL threats take 40% current HP (bosses 15%).",
    "card.pid_hijack.desc":
      "Target reverses, dealing 120 DPS to everything it passes.",
    "card.gc_force.desc":
      "",
    "card.logic_bomb.desc":
      "Detonates after 2s — AoE scales with cluster density.",
    "card.backdoor.desc":
      "The 5 furthest-advanced threats yanked back to spawn.",
    "card.fork_bomb.desc":
      "spreads DoT + slow through the swarm",
    "card.entropy_tax.desc":
      "",
    "card.dead_mans_switch.desc":
      "When integrity drops below 3, ALL damage doubles until wave end.",
    // GA-10 (Spec C+) — card.headline localization: the short impact line shown
    // on the tooltip/card `impact` slot. EN mirrors cards.json (never regresses).
    "card.overload_malware.headline": "+20% FIRE DMG",
    "card.overload_ice.headline": "+20% ICE DMG",
    "card.overload_emp.headline": "+20% BEAM DMG",
    "card.firewall_boost.headline": "+1 INTEGRITY",
    "card.data_mining.headline": "+20% CREDITS / KILL",
    "card.overclock.headline": "+20% FIRE RATE",
    "card.cache_hit.headline": "+15% RANGE",
    "card.packet_burst.headline": "+75¢ INSTANT",
    "card.packet_inspector.headline": "ALL HP BARS VISIBLE",
    "card.piercing_shots.headline": "+1 PIERCE",
    "card.throttle_field.headline": "−15% ENEMY SPEED",
    "card.synergy_bridge.headline": "8-DIR SYNERGIES",
    "card.chain_reaction.headline": "1.5× SYNERGY RATE",
    "card.bounty_protocol.headline": "+40% CREDITS",
    "card.emergency_patch.headline": "+3 INTEGRITY",
    "card.intrusion_scanner.headline": "SCAN 3 WAVES",
    "card.firewall_reinforce.headline": "FIREWALLS −10% SPEED",
    "card.thread_priority.headline": "TARGET: FASTEST",
    "card.greed_protocol.headline": "+60% CREDITS / −20% DMG",
    "card.minimalist.headline": "+15% DMG / EMPTY SLOT",
    "card.full_refund.headline": "100% SELL REFUND",
    "card.quantum_surge.headline": "2× ALL DMG",
    "card.hyper_threading.headline": "2× FIRE RATE",
    "card.root_access.headline": "40% SPEED",
    "card.zero_day_exploit.headline": "+50% vs BOSS/GOLEM",
    "card.memory_overflow.headline": "+1 DRAFT CARD",
    "card.glass_protocol.headline": "+60% DMG / −2 INTEGRITY",
    "card.scorched_earth.headline": "+30% DMG / −50% CREDITS",
    "card.resonance.headline": "+12% DMG / SYNERGY",
    "card.memory_leak.headline": "−40% HP · EXPOSE 5s",
    "card.cascade.headline": "SYNERGY → 25 AOE SPLASH",
    "card.late_overclock.headline": "+35% FIRE RATE",
    "card.neural_nexus.headline": "TRI-CLUSTER AUTO-KILL",
    "card.time_warp.headline": "[T] 5s FREEZE · 1/WAVE",
    "card.recursive_loop.headline": "10TH KILL → 25% AOE",
    "card.apex_predator.headline": "+120% MAIN / −40% OTHER",
    "card.monoculture.headline": "RESONANCE ANYWHERE / −25% DMG",
    "card.kernel_panic.headline": "40% HP NUKE · BOSS 15%",
    "card.pid_hijack.headline": "HIJACK · RAMPAGE · ◎ AIM",
    "card.gc_force.headline": "PURGE <25% · +5¢/KILL",
    "card.logic_bomb.headline": "DELAYED AOE · ◎ AIM",
    "card.backdoor.headline": "YANK 5 → SPAWN",
    "card.fork_bomb.headline": "CONTAGION · ◎ AIM",
    "card.entropy_tax.headline": "+25% ALL DMG",
    "card.dead_mans_switch.headline": "2× DMG WHEN <3 LIVES",
    // Spawn alert (CR2)
    "spawn.alert.line1": "WARNING: New threat vector detected.",
    "spawn.alert.line2": "Network leak imminent.",
    "spawn.alert.dismiss": "ACKNOWLEDGED",
    // Sell dialog
    "sell.invested": "INVESTED",
    "sell.refund": "REFUND",
    "sell.pathWarning": "PATH WILL RECALCULATE",
    "sell.confirm": "CONFIRM SELL",
    "sell.cancel": "CANCEL",
    "sell.towerTitle": "DECOMMISSION TOWER",
    "sell.firewallTitle": "DECOMMISSION FIREWALL",
    // Early-game threat diagnostics (P23)
    // S126 — the ECHELON corruption act tells (render-only narration)
    // Task-004 (2026-threat-profile-model, AC-10) — the five threat-level
    // names (see `ThreatLevelId` in core/MetaState.ts for the canonical id
    // order/spelling). Owner decision 2026-08-14: names stay English in BOTH
    // locales (OQ-4 precedent, 2026-gameplay-ui-compression), uppercase. §28
    // (AC-11) forbids any digit or comparative word under this whole
    // `threatLevel.` namespace — these five are pure identity, no numbers.
    "threatLevel.sandbox": "SANDBOX",
    "threatLevel.containment": "CONTAINMENT",
    "threatLevel.lockdown": "LOCKDOWN",
    "threatLevel.overrun": "OVERRUN",
    "threatLevel.fallout": "FALLOUT",
    // Task-004 (2026-prestart-deployment-redesign, AC-20/AC-21a) — the two
    // lock languages the threat rail's tiles use (never mixed — AC-20). Both
    // registered as TEMPLATES with the repo's {{n}} placeholder, exactly the
    // `prerun.deploy.need.operator` pattern above: the §28 guard in
    // ThreatLevelI18n.test.ts rejects any digit under `threatLevel.`, so the
    // unlock count is substituted at render time, never stored here.
    "threatLevel.lock.release": "NOT RELEASED",
    "threatLevel.lock.runs": "{{n}} MORE SORTIES",
  });

  registerTranslations("de", {
    "hud.credits": "¢ {{value}}",
    // S146/BL-140 — Zell-Label im linken HUD über dem goldenen ¢-Wert.
    "hud.creditsLabel": "GUTHABEN",
    // S146/BL-140 — a11y-Gruppenname für das linke HUD.
    "hud.statusLabel": "MAINFRAME-STATUS",
    "hud.lives": "♥ {{value}}",
    "hud.integrity": "INTEGRITÄT",
    "a11y.coreHit": "Mainframe getroffen — Integrität {{value}}",
    "hud.wave": "WELLE {{value}}",
    "hud.waveMax": "/ {{value}}",
    // BL-121 — die dauerhafte, auf-einen-Blick Akt-Anzeige
    // D-335 correction (docs/decisions.md, amended) — der Besitzer wollte das
    // WORT ("AKT I"), keine bloße Ziffer; siehe HUD.ts's refreshActBadge und
    // den EN-Block oben für die gemessene Begründung (live pro Gerät
    // entschieden, kein statisch verschifftes Ziffer-only-String).
    "hud.actPrefix": "AKT",
    "hud.act.1": "I",
    "hud.act.2": "II",
    "hud.act.3": "III",
    "hud.score": "PUNKTE {{value}}",
    "hud.endlessFrozen": "PUNKTE EINGEFROREN — ENDLOS",
    // BL-63 — das Score-Rennen + die Multiplikator-Transparenz.
    "hud.mult": "MULT",
    "hud.multIdle": "keine Kette",
    "hud.mult.tip.name": "PUNKTE-MULTIPLIKATOR",
    "hud.mult.tip.impact": "Kettenkills erhöhen ihn",
    "hud.mult.tip.how1": "Jeder Kill öffnet ein kurzes Fenster — töte weiter, bevor der Balken leer ist, um die Stufen zu steigern.",
    "hud.mult.tip.how2": "Eine Lücke (Balken leer) setzt die Kette auf ×1 zurück.",
    "hud.mult.tip.how3": "Höhere Kette → größerer Punkte-Multiplikator.",
    "hud.chain": "{{value}}-KILL-KETTE",
    "hud.multWindow": "+FENSTER",
    "hud.multBoost": "+MULT",
    "hud.worldLead": "⚑ WELTSPITZE",
    "hud.youAreNumber1": "du BIST #1",
    "hud.you": "DU",
    "hud.vsRank": "vs #1 · {{holder}} {{value}}",
    "hud.vsPersonal": "vs BEST · {{value}}",
    "hud.rank1": "#1",
    // BL-109 — Textgröße (a11y) — siehe EN-Block.
    "gameover.title": "SYSTEM KOMPROMITTIERT",
    "gameover.subtitle": "MAINFRAME-INTEGRITÄT: NULL",
    "gameover.score": "PUNKTE: {{value}}",
    "gameover.campaignResult": "↑ KAMPAGNE ABGESCHLOSSEN — ERGEBNIS WELLE {{value}}",
    "score.bonus.label": "INTEGRITÄTS-BONUS",
    "score.bonus.value": "+{{value}}",
    "score.logged": "↑ IM GLOBALEN VERTEIDIGUNGSLOG VERZEICHNET",
    "unlock.newAi": "NEUE KI ENTSCHLÜSSELT",
    "unlock.deployable": "JETZT EINSETZBAR",
    "gameover.restart": "RUN NEU STARTEN",
    "gameover.mainMenu": "HAUPTMENÜ",
    "gameover.stat.wave": "HÖCHSTE WELLE",
    "gameover.stat.kills": "BEDROHUNGEN NEUTRALISIERT",
    "gameover.stat.towers": "KNOTEN ERRICHTET",
    "gameover.stat.gold": "CREDITS VERDIENT",
    "gameover.stat.echelon": "ECHELON-SIGNATUR",
    "gameover.stat.echelonResident": "RESIDENT",
    "gameover.carrier": "TRÄGERSIGNAL NOCH OFFEN — SIE BLEIBT.",
    "gameover.onRecord": "WELLE {{value}} — IM LOG VERZEICHNET.",
    "gameover.comicAlt":
      "Comic-Panel: Magenta-Schaltbahnen überwuchern die Mainframe-Halle, ECHELONs riesiges Schaltring-Auge blickt herab — der Kern flackert, der Bildschirm gehört ihr.",
    // S130 — die Akt-Übergangskarten (Story Bible §6.4)
    "act.card.2.title": "AKT II — IHR SIGNAL",
    "act.card.2.sub": "SIGNATUR-ANOMALIE — EINE ZWEITE SIGNATUR REITET IM STROM",
    "act.card.3.title": "AKT III — DOMINION",
    "act.card.3.sub": "GRID-AUTORITÄT KOMPROMITTIERT — HALTE DIE LINIE",
    "victory.title": "MAINFRAME GESICHERT",
    "victory.subtitle": "INTRUSION ABGEWEHRT",
    "victory.stat.wave": "WELLE ABGESCHLOSSEN",
    "victory.stat.score": "ENDPUNKTZAHL",
    "victory.stat.echelon": "ECHELON-SIGNATUR",
    "victory.stat.echelonPurged": "GELÖSCHT",
    "victory.exit": "BEENDEN",
    "victory.restart": "NEUSTART",
    "victory.continue": "ECHO-MODUS (ENDLOS)",
    "victory.epilogue": "DAS LOG VERZEICHNET: DU HAST GEHALTEN.",
    "victory.echoHint": "IHR ECHO BLEIBT IN DEN LEITUNGEN.",
    "victory.comicAlt":
      "Comic-Panel: Der Mainframe-Kern leuchtet wieder grün, die letzte Magenta-Korruption löst sich auf — ECHELONs halbgeformtes Auge zerfällt über der Halle.",
    "wave.indicator": "▶ WELLE {{value}} STARTEN",
    "wave.build.phase": "AUFBAUPHASE",
    "wave.build.hint": "VERTEIDIGUNG PLATZIEREN",
    "coach.tapTower": "TURM ANTIPPEN",
    "coach.tapPlace": "ZUM PLATZIEREN TIPPEN",
    "draft.banner": "// ABGEFANGENER SKILL — ZUM INSTALLIEREN WÄHLEN",
    "draft.skip": "VERWERFEN (+50¢)",
    "draft.installed": "▸ INSTALLIERT",
    "payload.header": "// PAYLOAD",
    "payload.armed": "GELADEN",
    "payload.deploy": "AUSLÖSEN",
    "payload.timewarp": "ZEITSPRUNG",
    "payload.how.global": "Sofort aktiv — feuert automatisch (kein Zielen).",
    "payload.how.targetEnemy": "◎ ZIELEN — Gegner anklicken zum Auslösen.",
    "payload.how.targetCell": "◎ ZIELEN — Rasterzelle anklicken zum Auslösen.",
    "payload.how.timewarp": "Sofort aktiv — friert alle Gegner ein (kein Zielen).",
    "payload.timewarp.impact": "Friert ALLE Gegner für einige Sekunden ein",
    "payload.rackFull": "PAYLOAD-RACK VOLL — erst eins auslösen",
    "payload.armed.banner": "{{name}} GELADEN — klicke zum Auslösen",
    "skill.deck.empty": "LEER",
    "skill.overview.open": "SKILLS [S]",
    "audio.music": "MUSIK",
    "audio.sound": "TON",
    "audio.volume": "LAUTSTÄRKE",
    "audio.audio": "AUDIO",
    "audio.mute": "Audio stummschalten",
    "audio.unmute": "Audio einschalten",
    "hud.pause": "⏸ PAUSE",
    "hud.resume": "▶ WEITER",
    "hud.speed.1x": "1×",
    "hud.speed.2x": ">> 2×",
    // BL-63 — das ⚙ Zahnrad (früher: die umgezogenen Steuerelemente des
    // Dropdowns; seit Spec 2026-pause-settings-overlays Task-003 öffnet das
    // Zahnrad das EINSTELLUNGS-MODAL und liest die Schlüssel dort).
    "hud.gear": "OPTIONEN",
    "hud.pauseMenu": "PAUSENMENÜ",
    "hud.audio.section": "// AUDIO",
    "hud.muteAll": "ALLES STUMM",
    "hud.restartRun": "↻ RUN NEU STARTEN",
    "hud.exitToMenu": "⏻ ZURÜCK ZUM MENÜ",
    // Task-012 (AC-16, O-11b) — die Zahnrad-Zeile für das Archiv der Bedrohungen.
    "hud.library": "◈ BEDROHUNGSARCHIV",
    "library.title": "BEDROHUNGSARCHIV",
    "library.count": "{{known}}/{{total}} BEKANNT",
    "library.close": "✕ SCHLIESSEN",
    "library.unknown": "SIGNAL UNBEKANNT",
    "library.encrypted": "VERSCHLÜSSELT",
    "library.opened": "Bedrohungsarchiv geöffnet.",
    // D-340 — siehe EN-Block: der leere Zustand benennt sich selbst.
    "library.redacted.title": "KEIN DOSSIER",
    "library.redacted.body":
      "Zu diesem Signal ist nichts erfasst. Dossiers entstehen beim Erstkontakt — halte die Leitung, dann füllt sich das Archiv von selbst.",
    // BL-115 — der Laufzeit-Sprachumschalter (Beschriftung des EN/DE-Schalters).
    "setting.language": "SPRACHE",
    // BL-130 — das PreRun-Zahnrad-Menü: Abschnittsüberschrift + die jetzt
    // lokalisierte RESET-PROGRESS-Steuerung im Zahnrad.
    "setting.section": "// EINSTELLUNGEN",
    "prerun.reset": "⟲ FORTSCHRITT LÖSCHEN",
    "prerun.resetConfirm": "⚠ WIRKLICH LÖSCHEN?",
    // Spec 2026-pause-settings-overlays (AC-3) — Titel + Unterzeile des
    // Pause-Modals, wörtlich aus dem Owner-Mockup. `pause.message` ist mit
    // Task-003 ENTFALLEN: sein letzter Leser war der Abschnittskopf des
    // HUD-Zahnrad-Dropdowns (`HUD.ts:1177` @ d888033 — historische Fundstelle,
    // die Aufrufstelle existiert nicht mehr); das Dropdown gibt es nicht mehr.
    "pause.title": "PAUSIERT",
    "pause.subtitle": "Klicken oder ESC zum Fortsetzen",
    "pause.subtitleTouch": "Tippen zum Fortsetzen",
    // Spec 2026-pause-settings-overlays (AC-4) — das Einstellungs-Modal,
    // wörtlich aus dem Owner-Mockup. `// AUDIO`, `ALLES STUMM` und
    // `◈ BEDROHUNGSARCHIV` sind die verschifften Dropdown-Strings, unverändert
    // umgezogen; nur diese fünf sind neu. `// STEUERUNG`
    // (hud.controls.section) entfällt mit dem Dropdown — RESTART und EXIT
    // wohnen jetzt im Pause-Modal.
    "settings.title": "EINSTELLUNGEN",
    "settings.subtitle": "Audio & Spieloptionen",
    "settings.game.section": "// SPIEL",
    "settings.apply": "✔ ÜBERNEHMEN",
    "settings.back": "ZURÜCK",
    "skilldeck.header": "// LOADOUT",
    // D-333 — the header's trailing open-overview glyph (retired the
    // standalone "SKILLS [S]" row; the header itself is now the tap target).
    "skilldeck.headerHint": "[S]",
    "spawn.icon": "⚠",
    "prerun.operator": "OPERATOR",
    "prerun.modules": "LOADOUT-MODULE",
    "prerun.selectModules": "3 MODULE WÄHLEN",
    "prerun.moduleSlot": "SLOT {{n}}",
    "prerun.modulesSelected": "MODULE GEWÄHLT",
    "prerun.modcat.economy": "WIRTSCHAFT",
    "prerun.modcat.combat": "KAMPF",
    "prerun.modcat.analytics": "ANALYTIK",
    "prerun.modcat.defense": "VERTEIDIGUNG",
    "prerun.modcat.adaptive": "ADAPTIV",
    "prerun.modcat.system": "SYSTEM",
    "prerun.locked": "GESPERRT",
    // S188/BL-209 — Loadout = 3 Slots + ausklappbares Modul-Fach.
    "prerun.loadout.add": "MODUL HINZUFÜGEN",
    "prerun.loadout.done": "FERTIG",
    "prerun.loadout.empty": "FREIER SLOT",
    // REMOVED 2026-08-16 — siehe die EN-Notiz: `prerun.briefing.*` wird im
    // Zwei-Screen-Flow nicht weitergefuehrt.
    "prerun.deployGrid": "ABWEHRGITTER STARTEN",
    "prerun.deploySub": "VERTEIDIGUNGSPROTOKOLL INITIALISIEREN",
    // Die Aktnamen bleiben in BEIDEN Sprachen englisch — dieselbe
    // Owner-Entscheidung wie bei den Stufennamen (2026-08-14): sie sind
    // Titel der Sequenz, keine Beschriftung. WEITER ist übersetzt, weil es
    // eine Handlung ist.
    "prerun.loadout": "MODULE",
    "prerun.tapToChange": "TIPPEN ZUM ÄNDERN",
    "prerun.act1": "BUILD YOUR OPERATOR",
    "prerun.act2": "FACE THE THREAT",
    "prerun.advance": "WEITER",
    "prerun.operatorId": "OPERATOR-ID",
    "prerun.enterInitials": "INITIALEN / ID EINGEBEN",
    "prerun.tapInitials": "▲▼ TIPPEN FÜR ID",
    "prerun.enemyAnalysis": "GEGNER-ANALYSE",
    "prerun.enemyAnalysis.livetag": "LIVE · CH-66",
    "comms.signalOverride": "SIGNALÜBERNAHME · UNBEKANNTE QUELLE",
    // S193 (BL-273): OPERATOR/OVERMIND/SIGNAL sind im Deutschen dieselben
    // Broadcast-Begriffe (Lehnwörter, das "LIVE · CH-66"-Muster).
    "comms.chrome.channelVera": "CH-01 · MAINFRAME-FEED",
    "comms.chrome.channelFenrir": "CH-66 · FEINDSIGNAL-ABGRIFF",
    "comms.chrome.roleOperator": "OPERATOR",
    "comms.chrome.roleOvermind": "OVERMIND",
    "comms.chrome.roleSignal": "SIGNAL",
    "prerun.role.sentinel-7": "DEFENSIVER WÄCHTER",
    "prerun.role.warden": "TAKTISCHER NETRUNNER",
    "prerun.role.ghost": "TARN-INFILTRATOR",
    "prerun.role.architect": "SCHWERER JUGGERNAUT",
    "prerun.role.cipher": "RÄTSELHAFTE CHIFFRE",
    "prerun.selected": "AUSGEWÄHLT",
    "prerun.prevOp": "Vorheriger Operator",
    "prerun.nextOp": "Nächster Operator",
    "prerun.callsign_label": "OPERATOR IM LOG",
    // BL-200 — die Deploy-Bereitschaftsliste (Hover/Fokus/Touch). Zeigt die noch
    // fehlenden Schritte vor dem Start; jede Zeile trägt einen ✓/✗-Status.
    "prerun.deploy.checklist": "DEPLOY-CHECKLISTE",
    "prerun.deploy.ready": "ALLE SYSTEME BEREIT — DEPLOY.",
    "prerun.deploy.step.initials": "INITIALEN",
    "prerun.deploy.step.model": "KI-MODELL",
    "prerun.deploy.step.modules": "MODULE",
    "prerun.deploy.need.initials": "INITIALEN EINGEBEN",
    "prerun.deploy.need.modules": "3 MODULE WÄHLEN ({{n}}/3)",
    // BL-218 — ein gesperrter Operator kann im Karussell betrachtet, aber nicht
    // deployt werden; das Gate nennt die noch fehlenden Freischalt-Runs.
    "prerun.deploy.need.operator": "ENTSCHLÜSSELN IN {{n}} RUNS",
    "prerun.operatorLocked": "OPERATOR VERSCHLÜSSELT",
    // BL-218 — der coole In-World-Hinweis auf einer gesperrten Operator-Karte
    // (neben dem Schloss): wie diese KI online gebracht wird.
    "prerun.lock.title": "VERSCHLÜSSELT",
    "prerun.lock.note": "Spiele {{n}} weitere Einsätze, um diese KI zu entschlüsseln",
    // S129 / D-227 — Rechtliches & Datenschutz + BL-96 Hinweis vor dem Absenden.
    "legal.open": "RECHTLICHES",
    "legal.title": "RECHTLICHES & DATENSCHUTZ",
    "legal.close": "SCHLIESSEN",
    "prerun.privacyNotice.more": "DATENSCHUTZ ▸",
    // BL-202 — der VERA⇄FENRIR-Block. VERAs Rollenzeile (ihr Desk) + FENRIRs
    // BEDROHUNGSDOSSIER (das Profil, das seine Box füllt; Lore aus STORY_BIBLE §2 —
    // der Wolf, ECHELONs Herold, Befehlshaber jedes feindlichen Prozesses).
    "prerun.vera.desk": "ABWEHR-DESK",
    "prerun.fenrir.class": "KLASSE",
    "prerun.fenrir.classV": "ECHELON-HEROLD — DER WOLF",
    "prerun.fenrir.command": "BEFEHL",
    "prerun.fenrir.commandV": "ALLE FEINDLICHEN PROZESSE",
    "prerun.fenrir.signal": "SIGNAL",
    "prerun.fenrir.signalV": "CH-66 · FEINDLICHER FUNK",
    // S188 — FENRIR Gegner-Analyse Statusbalken (Premium-Bedrohungskarte).
    "prerun.fenrir.threatLevel": "BEDROHUNG",
    "prerun.fenrir.offensive": "OFFENSIVE",
    "prerun.fenrir.defensive": "DEFENSIVE",
    "prerun.fenrir.swarm": "SCHWARM-KAPAZITÄT",
    "prerun.fenrir.tech": "TECH-ANPASSUNG",
    "prerun.fenrir.intel": "GEGNER-INTEL",
    "prerun.step2": "OPERATOR",
    "prerun.step2_hint": "RUNS ABSCHLIESSEN, UM WEITERE OPERATOREN FREIZUSCHALTEN",
    // Operator-Blurbs (BL-114) — MÜSSEN die echten OperatorEffects spiegeln.
    "operator.sentinel-7.desc": "+1 HP  |  +5% REICHWEITE",
    "operator.warden.desc": "+50 CREDITS  |  -1 HP",
    "operator.ghost.desc": "+15% SYNERGIE  |  -10% SCHADEN",
    "operator.architect.desc": "85% VERKAUFS-ERSTATTUNG  |  +25 CREDITS",
    "operator.cipher.desc": "+8% REICHWEITE  |  +10% SYNERGIE",
    "module.overclocked_boot.name": "OVERCLOCKED BOOT",
    "module.overclocked_boot.desc": "+100 Startguthaben",
    "module.pattern_recognition.name": "PATTERN RECOGNITION",
    "module.pattern_recognition.desc": "Turm-Reichweite +5%",
    "module.quick_learner.name": "QUICK LEARNER",
    "module.quick_learner.desc": "Punktegewinn +15%",
    "module.network_optimizer.name": "NETWORK OPTIMIZER",
    "module.network_optimizer.desc": "Synergie-Effekte +10%",
    "module.cache_warmth.name": "CACHE WARMTH",
    "module.cache_warmth.desc": "Erster Turm kostenlos",
    "module.failsafe_protocol.name": "FAILSAFE PROTOCOL",
    "module.failsafe_protocol.desc": "Einmal wiederbeleben bei 0 HP",
    "module.heuristic_bias.name": "HEURISTIC BIAS",
    "module.heuristic_bias.desc": "Draft zeigt 4 Karten",
    "module.defensive_default.name": "DEFENSIVE DEFAULT",
    "module.defensive_default.desc": "+1 Start-HP",
    "module.recursive_loops.name": "RECURSIVE LOOPS",
    "module.recursive_loops.desc": "Combo-Verfall -20%",
    "module.bit_packer.name": "BIT PACKER",
    "module.bit_packer.desc": "6 Skill-Slots",
    "module.cold_boot.name": "COLD BOOT",
    "module.cold_boot.desc": "Boss-Schaden -25% erste 5 Wellen",
    "module.adaptive_throttling.name": "ADAPTIVE THROTTLING",
    "module.adaptive_throttling.desc": "Qualität reduziert bei 14ms",
    // Enemy names (BL-113)
    "enemy.scout": "Datenpaket",
    "enemy.scout.short": "PKT",
    "enemy.golem": "Rootkit",
    "enemy.golem.short": "RTK",
    "enemy.swarm": "Nano-Schwarm",
    "enemy.swarm.short": "SWM",
    "enemy.shifter": "Polymorpher Wurm",
    "enemy.shifter.short": "PLY",
    "enemy.boss": "Core Breaker",
    "enemy.boss.short": "BRK",
    "enemy.final_boss": "AXIOM-77-Core",
    "enemy.final_boss.short": "AX7",
    "enemy.corrupted_node": "Korrumpierter Knoten",
    "enemy.corrupted_node.short": "CND",
    "enemy.cipher_breaker": "Chiffren-Brecher",
    "enemy.cipher_breaker.short": "CPH",
    "enemy.stealth_daemon": "Tarn-Daemon",
    "enemy.stealth_daemon.short": "STH",
    "enemy.firewall_breaker": "Firewall-Brecher",
    "enemy.firewall_breaker.short": "FWB",
    // BL-152 (S154) — die zwei neuen Akt-Bedrohungen.
    "enemy.jammer": "Störsender",
    "enemy.jammer.short": "JAM",
    "enemy.overseer": "Aufseher",
    "enemy.overseer.short": "OVS",
    // BL-155 (S158) — Threat Pack 2 (DE-Namen; das deutsche „Splitter" = Scherbe
    // ist der Sub, der Eltern-Gegner ist der „Spalter").
    "enemy.leech": "Egel",
    "enemy.leech.short": "LCH",
    "enemy.regenerator": "Regenerator",
    "enemy.regenerator.short": "RGN",
    "enemy.splitter": "Spalter",
    "enemy.splitter.short": "SPL",
    "enemy.splitling": "Splitter",
    "enemy.splitling.short": "SPN",
    // Task-009 (Spec B) — die drei neuen Daten-Archetypen. Der Hunter-Killer
    // bleibt unübersetzt (etablierter Terminus technicus, wie „Overmind");
    // Custodian → Verwalter (der panzernde Tresor), Siphon → Siphon (saugt
    // das Guthaben ab — dasselbe Lehnwort in beiden Sprachen).
    "enemy.hunter_killer": "Hunter-Killer",
    "enemy.hunter_killer.short": "HNK",
    "enemy.custodian": "Verwalter",
    "enemy.custodian.short": "CST",
    "enemy.siphon": "Siphon",
    "enemy.siphon.short": "SPH",
    // BL-161 (S162) — die Bedrohungs-Dossier ROLLEN-Tags (deriveRole). Knappe
    // Großbuchstaben im Cyber/Hacker-Register, an die enemy.*-DE-Namen angelehnt.
    "enemy.role.overmind": "FINALER KERN · OVERMIND",
    "enemy.role.apex": "APEX-BEDROHUNG",
    "enemy.role.spawner": "SPAWNER",
    "enemy.role.shifter": "WANDLER",
    "enemy.role.suppressor": "STÖRSENDER",
    "enemy.role.commander": "KOMMANDEUR",
    "enemy.role.siphon": "SAUGER",
    "enemy.role.mender": "FLICKER",
    "enemy.role.divider": "TEILER",
    "enemy.role.breacher": "BRECHER",
    "enemy.role.heavy": "SCHWER",
    "enemy.role.rusher": "STÜRMER",
    "enemy.role.intruder": "EINDRINGLING",
    // BL-161 (S162) — der Dossier-FÄHIGKEITSblock (deriveAbility): Titel + Text.
    // Symbol-Präfix im Titel; {{count}}/{{dmg}} bleiben datengetrieben (D-256).
    "enemy.ability.overmind.title": "⟁ OVERMIND",
    "enemy.ability.overmind.desc":
      "Teilt sich bei 70% / 30% Integrität in Phasen; jede Phase spawnt eine frische Welle Adds. Das letzte Aufgebot des Mainframes.",
    "enemy.ability.spawn_adds.title": "⊕ ADDS SPAWNEN",
    "enemy.ability.spawn_adds.desc":
      "Bricht mitten im Run in {{count}}weitere Bedrohungen auf — erledige ihn, bevor er den Schwarm vervielfacht.",
    "enemy.ability.polymorph.title": "✷ POLYMORPH",
    "enemy.ability.polymorph.desc":
      "Wechselt seine Resistenz in Intervallen — kein einzelnes Element hält lange gegen ihn.",
    "enemy.ability.suppression_field.title": "▾ STÖRFELD",
    "enemy.ability.suppression_field.desc":
      "Stört nahe Türme — drosselt ihre Feuerrate in Reichweite. Verteile deine Türme oder leg ihn zuerst um.",
    "enemy.ability.aura_host.title": "❖ AURA-TRÄGER",
    "enemy.ability.aura_host.desc":
      "Schirmt das Rudel um sich ab — nahe Bedrohungen nehmen weniger Schaden. Töte den Träger, und die Aura bricht zusammen.",
    "enemy.ability.credit_siphon.title": "◎ KREDIT-SAUGER",
    "enemy.ability.credit_siphon.desc":
      "Zapft deine Credits ab, solange er lebt — der Fluch des Hamsterers. Töte ihn schnell, um die Blutung zu stoppen.",
    "enemy.ability.self_repair.title": "✚ SELBSTREPARATUR",
    "enemy.ability.self_repair.desc":
      "Flickt seine Integrität jede Sekunde zusammen — Streuschaden hält ihn nicht. Brich ihn mit Burst.",
    "enemy.ability.fracture.title": "⊟ AUFSPALTUNG",
    "enemy.ability.fracture.desc":
      "Sein Tod spaltet ihn in {{count}}schnelle Splitter — bring Flächenschaden mit, sonst flutet er die Bahn.",
    "enemy.ability.firewall_breach.title": "▣ FIREWALL-DURCHBRUCH",
    "enemy.ability.firewall_breach.desc":
      "Reißt bei Kontakt {{dmg}} aus der Firewall — ein gezielter Brecher, fang ihn früh ab.",
    "enemy.ability.hardened.title": "◼ GEPANZERT",
    "enemy.ability.hardened.desc":
      "Schwer gepanzert — steckt Treffer weg. Bündle das Feuer, um den Panzer zu knacken.",
    "enemy.ability.rush.title": "» STURM",
    "enemy.ability.rush.desc":
      "Schnell und leicht — er rast die Bahn entlang. Verlangsame ihn oder fang ihn früh ab.",
    "enemy.ability.intrusion.title": "· INTRUSION",
    "enemy.ability.intrusion.desc":
      "Ein Standard-Intrusionspaket — die Grundbedrohung auf der Leitung.",
    // Task-002 (2026-threat-archive-console) — FENRIRs kalte Antagonisten-
    // Anmoderationen, echte DE-Fassungen (Mockup-Vorschläge als Ausgangspunkt,
    // spec.md Assumption), nicht die rohe EN-Zeile (der Build-81-Befund #6).
    "enemy.taunt.new_toy": "Triff mein neues Spielzeug.",
    "enemy.taunt.unseen": "Diesen hier kennst du noch nicht.",
    "enemy.taunt.something_new": "Etwas Neues für dich.",
    "enemy.taunt.analyse_now": "Analysier es, solange du kannst.",
    "enemy.taunt.try_stop": "Versuch, das aufzuhalten.",
    "enemy.taunt.fresh_nightmare": "Ein frischer Albtraum.",
    // BL-161 (S162) — die statischen Dossier-Karten-Labels (Statzeile + Matchup-
    // Zeile + Klassifizierung + Leer-Platzhalter).
    "card.label.classification": "⚠ BEDROHUNGS-MANIFEST",
    "card.label.integrity": "INTEGRITÄT",
    "card.label.velocity": "TEMPO",
    "card.label.bounty": "KOPFGELD",
    "card.label.weakness": "SCHWÄCHE",
    "card.label.resist": "RESISTENZ",
    "card.label.immune": "IMMUN",
    "card.label.none": "— keine —",
    // BL-172 — die Schild-Enthüllungskarte (EnemyReveal.buildShieldItem), das
    // Epic-Dossier des W15+-Schildmechanismus. Prosa + Wort-Labels im Cyber/
    // Hacker-Register; {{wave}} bleibt datengetrieben. Code-Tokens (CLASS-S,
    // SENTINEL-7, die ◈/⚠/·-Symbole) bleiben über beide Locales identisch.
    "shield.classification": "⚠ ABWEHRPROTOKOLL",
    "shield.name": "SCHILD",
    "shield.subtitle": "VERSTÄRKT · HEX-KÄFIG",
    "shield.stat.protocol.k": "PROTOKOLL",
    "shield.stat.protocol.v": "VERSTÄRKT",
    "shield.stat.online.k": "ONLINE",
    "shield.stat.online.v": "WELLE {{wave}}+",
    "shield.ability.title": "◈ HEX-KÄFIG",
    "shield.ability.desc":
      "Ein facettierter Schild fängt Schaden ab, bevor der Kern der Bedrohung berührt wird. Brich den Käfig mit Dauerfeuer auf, dann setz den Kill.",
    "shield.footerLeft": "SENTINEL-7 · SCHILD",
    "shield.flavour": "Manche von ihnen kamen gepanzert zurück.",
    // Boot Sequence
    "boot.pressKey": "LEERTASTE ZUM BOOTEN",
    "boot.tapKey": "ZUM BOOTEN TIPPEN",
    "boot.lore1": "AXIOM-77 ist erwacht.",
    "boot.lore2": "Der letzte Mainframe steht.",
    "boot.lore3": "Du bist SENTINEL-7.",
    "boot.signInToCompete": "GLOBAL — ZUM MITSPIELEN ANMELDEN",
    "boot.localBest": "LOKALER REKORD: {{value}}",
    "boot.viewGlobalRanks": "GLOBALE RÄNGE ANZEIGEN",
    // Boss system
    "countdown.hint": "LEERTASTE FÜR FRÜHSTART (+{{value}} ¢)",
    "countdown.earlyBonus": "FRÜHSTART +{{value}} ¢",
    "stealth.warning": "⚠ STEALTH-EINHEITEN ERKANNT — SIGNAL-TURM PLATZIEREN",
    // S195/BL-282 — Boss-Auftritt + die kanonischen Banner auf i18n (S195-F11).
    "boss.arrival.title": "BOSS-DURCHBRUCH",
    "boss.incoming.banner": "⚠ BOSS IM ANMARSCH",
    "wave.processClear": "PROZESS BEREINIGT",
    "warning.coreBreaker": "!! WARNUNG: CORE BREAKER ERKANNT",
    "breach.eruptionImminent": "ERUPTION UNMITTELBAR // [{{col}},{{row}}]",
    "payload.instantFired": "SOFORT GEZÜNDET - kein Slot belegt",
    // S141 / D-236 — ECHELON-Mutations-Telegramme (Lever 1).
    "wave.mutation.hardened": "⚠ IHR SIGNAL: GEHÄRTETER STRANG",
    "wave.mutation.swift": "⚠ IHR SIGNAL: SCHNELLER STRANG",
    "wave.mutation.veiled": "⚠ IHR SIGNAL: VERHÜLLTER STRANG — SIGNAL EINSETZEN",
    "wave.mutation.splitting": "⚠ IHR SIGNAL: SPALTENDER STRANG",
    // BL-152 / BL-153 (S154) — das „NEUE BEDROHUNG"-Telegramm (einmal je Typ).
    "wave.newthreat.jammer": "⚠ NEUE BEDROHUNG: STÖRSENDER — SCHWÄCHT DEINE TÜRME",
    "wave.newthreat.overseer": "⚠ NEUE BEDROHUNG: AUFSEHER — SCHÜTZT NAHE GEGNER",
    // BL-155 (S158) — Threat Pack 2 (nennt die Bedrohung UND ihren Konter).
    "wave.newthreat.leech": "⚠ NEUE BEDROHUNG: EGEL — SAUGT DEINE CREDITS, SCHNELL TÖTEN",
    "wave.newthreat.regenerator": "⚠ NEUE BEDROHUNG: REGENERATOR — HEILT SICH, BURST IHN WEG",
    "wave.newthreat.splitter": "⚠ NEUE BEDROHUNG: SPALTER — TEILT SICH BEIM TOD, FLÄCHENSCHADEN",
    "firewall.destroyed": "FIREWALL DURCHBROCHEN",
    "breach.initiated": "⚠ BREACH-PROTOKOLL EINGELEITET",
    "breach.spawnOnline": "SPAWN-2 ONLINE",
    "breach.towerDestroyed":
      "KRITISCHER FEHLER: Fundament Kompromittiert // Struktur Verloren",
    "streak.flawless": "★ MAKELLOSE SERIE — +50% DRAFT-GLÜCK",
    // BL-241 — combo milestone callouts (×5/×7/×10)
    "combo.milestone.5": "COMBO ×5 — IN FLAMMEN",
    "combo.milestone.7": "COMBO ×7 — DOMINANT",
    "combo.milestone.10": "COMBO ×10 — UNAUFHALTSAM",
    // Spawn detection (Signal tower)
    // GA-08 (Spec C+) — siehe EN-Seite.
    "intel.revealWaves.header": "TIEFENSCAN // EINGEHENDE MANIFESTE ENTSCHLÜSSELT",
    "intel.revealWaves.line": "WELLE {{n}} // {{comp}}",
    "intel.revealWaves.none":
      "TIEFENSCAN // KEINE WEITEREN MANIFESTE — LETZTE WELLE ERREICHT",
    "spawn.detected":
      "NEUER VEKTOR ENTDECKT // NETZWERK-BREACH-ANKER LOKALISIERT",
    "spawn.forceRevealed": "⚠ KRITISCH: Unentdeckter Breach aktiv!",
    "signal.overload":
      "DIREKTER ABFANG // SIGNAL ÜBERLAST // KNOTEN EXPONIERT & EINHEIT ZERSTÖRT",
    // Turm-Labels + Tooltip-Rollen (BL-113)
    "tower.fire": "MALWARE",
    "tower.fire.role": "DOT / Schaden über Zeit",
    "tower.water": "ICE",
    "tower.water.role": "KETTE / Mehrfachziel",
    "tower.lightning": "EMP",
    "tower.lightning.role": "BURST / Krit",
    "tower.signal": "SIGNAL",
    "tower.signal.role": "SUPPORT / Debuff (kein direkter Schaden)",
    "tower.kinetic": "Rail-Sentinel",
    "tower.kinetic.role": "Präzisions-Railgun",
    "tower.virus": "Seuchen-Knoten",
    "tower.virus.role": "Ballistische AoE-Infektion",
    // BL-213 (S189) — glanceable one-word ROLE for the bay tooltip impact head.
    "tower.fire.tag": "DOT",
    "tower.water.tag": "KETTE",
    "tower.lightning.tag": "BURST",
    "tower.signal.tag": "SUPPORT",
    "tower.kinetic.tag": "RAILGUN",
    "tower.virus.tag": "AOE",
    // TowerSelector i18n (BL-13)
    "tower.header": "// EINSATZ",
    "tower.firewall": "FIREWALL",
    "tower.firewall.role": "BARRIERE / Pfadblocker",
    "tower.firewall.tag": "BARRIERE",
    "tower.firewall.support": "PASSIVE STRUKTUR · kein direkter Schaden",
    "tower.firewall.special":
      "Blockiert Feindpfade. Strategisch platzieren, um Feinde auf längere Routen an deinen Türmen vorbei zu zwingen. Kann von Firewall-Brechern zerstört werden – verteidige sie.",
    "tower.firewall.tip":
      "TIPP: Kombiniere mit Engpässen, um Turmabdeckung zu maximieren. Feinde müssen um Firewalls herumlaufen.",
    "tower.support": "SUPPORT-TURM · kein direkter Schaden",
    "tower.cost": "Kosten: ¢{{cost}}",
    "tower.upgrade.max": "MAX STUFE",
    "tower.upgrade": "UPGRADE · ¢{{cost}}",
    "tower.upgrade.insufficient": "UPGRADE · ¢{{cost}} NICHT GENUG",
    "tower.sell": "VERKAUF ¢{{price}}",
    "tower.sell.label": "VERKAUF",
    "tower.sell.price": "¢{{price}}",
    // BL-297 — das Label des scharfgeschalteten Sell-Buttons (nicht das lange
    // `sell.confirm`, das der eigenständige Dialog weiter nutzt).
    "tower.sell.confirm": "BESTÄTIGEN",
    "tower.next": "NÄCHSTE",
    "tower.gold.tease": "Signaturfähigkeit am Gipfel",
    "tower.synergy": "Synergie: {{list}}",
    "tower.stats.support.range": "Reichweite: {{range}}",
    "tower.stat.dmg": "DMG",
    "tower.stat.dps": "DPS",
    "tower.stat.range": "Reichweite",
    "tower.stat.rate": "Rate",
    "tower.stat.eff": "EFF",
    "tower.stat.range.short": "REICHW",
    // ── BL-293 — der Mechanik-Block ist kein Satz mehr, sondern SCHLAGWORT +
    //    WERTE. Jede Zahl der alten Prosa überlebt, die Rahmung darum nicht.
    //    Deutsch ist die Entwurfssprache; kein Anglizismus, den der DE-Korpus
    //    nicht ohnehin führt (verlangsam*/Tempo bleiben, „Slow" kommt nicht).
    "tower.mech.dot.k": "BRAND",
    "tower.mech.dot.v": "{{dmg}} DMG / {{int}} s \u00b7 {{dur}} s \u00b7 {{total}} ges.",
    "tower.mech.chain.k": "KETTE",
    "tower.mech.chain.v": "+{{bounces}} Ziele \u00b7 80 px \u00b7 \u2212{{decay}} % je Sprung",
    "tower.mech.burst.k": "KRIT",
    "tower.mech.burst.v": "{{chance}} % \u00b7 \u00d7{{mult}} Schaden",
    "tower.mech.mark.k": "MARKIERUNG",
    "tower.mech.mark.v": "{{dur}} s \u00b7 +{{bonus}} % Schaden \u00b7 \u2212{{slow}} % Tempo",
    "tower.mech.railshot.k": "SCHIENE",
    "tower.mech.railshot.v": "{{charge}} s Ladung \u00b7 brettweit",
    "tower.mech.infection.k": "INFEKTION",
    "tower.mech.infection.v": "{{radius}} px \u00b7 {{tickDmg}}/{{int}} s \u00b7 {{dur}} s",
    "tower.mech.spread.k": "AUSBREITUNG",
    "tower.mech.spread.v": "{{chance}} % \u00b7 {{gen}} Gen. \u00b7 \u2212{{slow}} % Tempo",
    "tower.mech.pool.k": "POOL",
    "tower.mech.pool.v": "Pool {{pool}} s",
    // BL-293 — the L3 beat names, lifted out of the sentences that used to
    // hold them so the compressed capability row can carry name and value
    // separately. Extracted from the shipped `.on` strings, never re-typed.
    "tower.cap.fire.l3.name": "SENGEN",
    "tower.cap.water.l3.name": "KRYO-KETTE",
    "tower.cap.lightning.l3.name": "ÜBERLADUNG",
    "tower.cap.kinetic.l3.name": "PANZERDURCHSCHUSS",
    "tower.cap.signal.l3.name": "HONEYPOT",
    "tower.capv.fire": "Splash auf nahe Feinde",
    "tower.capv.water": "getroffene Feinde \u2212{{slow}} % Tempo",
    "tower.capv.lightning": "garantierter Krit alle {{period}} s",
    "tower.capv.kinetic": "ignoriert Rüstung",
    "tower.capv.signal": "lockt Markierte vom Pfad",
    "tower.capv.virus": "{{spread}} % Ausbreitung",
    // BL-182 / D-265 (S169) — die Fähigkeits-ARC je Turm: Name + eine konkrete
    // Wirkungsangabe, nie ein Satz (BL-296).
    "tower.cap.lv": "L{{level}}",
    "tower.cap.fire.l4.name": "FLÄCHENBRAND",
    "tower.cap.fire.l4.desc": "Brand springt auf Splash-Treffer",
    "tower.cap.fire.l5.name": "FEUERSTURM",
    "tower.cap.fire.l5.desc": "größerer, heißerer Splash",
    "tower.cap.water.l4.name": "TIEFKÜHLUNG",
    "tower.cap.water.l4.desc": "+1 Kettenziel",
    "tower.cap.water.l5.name": "SCHOCKFROST",
    "tower.cap.water.l5.desc": "Hauptziel eingefroren",
    "tower.cap.lightning.l4.name": "GABELUNG",
    "tower.cap.lightning.l4.desc": "Krit gabelt auf 2. Feind",
    "tower.cap.lightning.l5.name": "KETTENSTURM",
    "tower.cap.lightning.l5.desc": "Krit springt auf alle nahen",
    "tower.cap.kinetic.l4.name": "SCHUSSKORRIDOR",
    "tower.cap.kinetic.l4.desc": "trifft die ganze Linie",
    "tower.cap.kinetic.l5.name": "DOPPELSCHUSS",
    "tower.cap.kinetic.l5.desc": "Schiene feuert zweimal",
    "tower.cap.virus.l3.name": "VIRULENZ",
    "tower.cap.virus.l3.desc": "Ausbreitung steigt",
    // BL-175 / D-266 (S170) — die GOLD-Spitzenstufe (L6): das "GOLD"-Abzeichen +
    // die brandneue Signatur-Mechanik je Element (Name + Einzeiler). Die Namen
    // (HELIOS, ABSOLUTE ZERO, …) sind Eigennamen/Code-Token, bleiben also stehen.
    "tower.gold.badge": "GOLD",
    "tower.cap.fire.gold.name": "HELIOS",
    "tower.cap.fire.gold.desc": "Kills detonieren in Feuer",
    "tower.cap.water.gold.name": "ABSOLUTE ZERO",
    "tower.cap.water.gold.desc": "Nova friert eine Region ein",
    "tower.cap.lightning.gold.name": "TEMPEST",
    "tower.cap.lightning.gold.desc": "Blitze treffen die Stärksten",
    "tower.cap.kinetic.gold.name": "ANNIHILATOR",
    "tower.cap.kinetic.gold.desc": "exekutiert alles außer Bossen",
    "tower.cap.signal.gold.name": "ORACLE",
    "tower.cap.signal.gold.desc": "stärkt in Reichweite · enttarnt Stealth",
    "tower.cap.virus.gold.name": "PANDEMIC",
    "tower.cap.virus.gold.desc": "Infizierte platzen beim Tod",
    // Synergien (BL-113)
    "synergy.steam": "Dampfstoß",
    "synergy.steam.desc": "AoE 8 DMG · Tempo −40 %",
    "synergy.plasma": "Plasmabogen",
    "synergy.plasma.desc": "Strahl ×1,5 Schaden",
    "synergy.electroshock": "Elektroschock",
    "synergy.electroshock.desc": "30 % Betäubung · 0,8 s",
    "synergy.nexus": "Elementar-Nexus",
    "synergy.nexus.desc":
      "Aura 12 DMG · 50 px",
    "synergy.tracking": "Zielerfassung",
    "synergy.tracking.desc":
      "zielt zuerst auf Markierte",
    "synergy.broadcast": "Übertragung",
    "synergy.broadcast.desc":
      "Markierung springt · 48 px",
    "synergy.burning_mark": "Brandmal",
    "synergy.burning_mark.desc":
      "×2 auf Markierten",
    "synergy.designated": "Präzisionsschütze",
    "synergy.designated.desc":
      "×0,5 auf Markierte",
    "synergy.railarc": "Überladenes Geschoss",
    "synergy.railarc.desc":
      "Kill → 2 · 80 px",
    "synergy.wildfire": "Lauffeuer",
    "synergy.wildfire.desc":
      "brennen 4 · +20 % Ausbreitung",
    "synergy.waterborne": "Wassergetragen",
    "synergy.waterborne.desc":
      "×2 Reichweite · +1 Gen.",
    "synergy.plague": "Pest",
    "synergy.plague.desc":
      "Markierte: 100 % · ohne Limit",
    "synergy.resonance": "RESONANZ",
    "synergy.resonance.desc":
      "AoE-Impuls aus einem zusammenhängenden Turm-Cluster",
    // Kartennamen (BL-113 — Code-Bezeichner; etablierte Anglizismen bleiben)
    "card.overload_malware": "ÜBERLAST::MALWARE",
    "card.overload_ice": "ÜBERLAST::ICE",
    "card.overload_emp": "ÜBERLAST::EMP",
    "card.firewall_boost": "PATCH::HÄRTEN",
    "card.data_mining": "DATA_MINING.exe",
    "card.overclock": "OVERCLOCK",
    "card.cache_hit": "CACHE_HIT",
    "card.packet_burst": "PAKET_BURST",
    "card.packet_inspector": "PAKET_INSPEKTOR",
    "card.piercing_shots": "PANZERBRECHER.dll",
    "card.throttle_field": "DROSSELFELD",
    "card.synergy_bridge": "SYNERGIE_BRÜCKE",
    "card.chain_reaction": "KETTENREAKTION",
    "card.bounty_protocol": "KOPFGELD_PROTOKOLL",
    "card.emergency_patch": "NOTFALL_PATCH",
    "card.intrusion_scanner": "INTRUSION_SCANNER",
    "card.firewall_reinforce": "FIREWALL_VERSTÄRKUNG",
    "card.thread_priority": "THREAD_PRIORITÄT",
    "card.greed_protocol": "GIER_PROTOKOLL",
    "card.minimalist": "MINIMALIST",
    "card.full_refund": "VOLLE_ERSTATTUNG",
    "card.quantum_surge": "QUANTENSCHUB",
    "card.hyper_threading": "HYPER_THREADING",
    "card.root_access": "ROOT_ZUGRIFF",
    "card.zero_day_exploit": "ZERO_DAY_EXPLOIT",
    "card.memory_overflow": "SPEICHERÜBERLAUF",
    "card.glass_protocol": "GLAS_PROTOKOLL",
    "card.scorched_earth": "VERBRANNTE_ERDE",
    "card.resonance": "RESONANZ",
    "card.memory_leak": "SPEICHERLECK",
    "card.cascade": "KASKADE",
    "card.late_overclock": "THERMAL_RUNAWAY",
    "card.neural_nexus": "NEURALER_NEXUS",
    "card.time_warp": "ZEITSPRUNG",
    "card.recursive_loop": "REKURSIVE_SCHLEIFE",
    "card.apex_predator": "SPITZENPRÄDATOR",
    "card.monoculture": "MONOKULTUR",
    "card.kernel_panic": "KERNEL_PANIC",
    "card.pid_hijack": "PID_HIJACK",
    "card.gc_force": "gc --force",
    "card.logic_bomb": "LOGIKBOMBE",
    "card.backdoor": "BACKDOOR",
    "card.fork_bomb": "FORKBOMBE",
    "card.entropy_tax": "ENTROPIE_STEUER",
    "card.dead_mans_switch": "TOTMANNSCHALTER",
    // Kartenbeschreibungen (BL-113)
    "card.overload_malware.desc":
      "Verstärkt PULSE-Hitzekerne.",
    "card.overload_ice.desc":
      "Unterkühlt SCATTER-Arrays.",
    "card.overload_emp.desc":
      "stapelt mit Element-Fokus",
    "card.firewall_boost.desc":
      "kein Slot-Verbrauch",
    "card.data_mining.desc":
      "Zapft +20% Bonus-Credits aus jedem Kill ab.",
    "card.overclock.desc":
      "",
    "card.cache_hit.desc":
      "",
    "card.packet_burst.desc":
      "kein Slot-Verbrauch",
    "card.packet_inspector.desc":
      "",
    "card.piercing_shots.desc":
      "Verheerend mit SCATTER-Arrays.",
    "card.throttle_field.desc":
      "",
    "card.synergy_bridge.desc":
      "Synergien zünden jetzt in 8 Richtungen.",
    "card.chain_reaction.desc":
      "Resonanzkaskade: Synergie-Effekte zünden 3 Wellen lang mit 1,5-facher Frequenz.",
    "card.bounty_protocol.desc":
      "+40% Credit-Ausbeute auf Kills für 3 Wellen.",
    "card.emergency_patch.desc":
      "Sofort +3 Integrität wiederhergestellt.",
    "card.intrusion_scanner.desc":
      "",
    "card.firewall_reinforce.desc":
      "Aktive Firewalls verlangsamen angrenzende Bedrohungen dauerhaft um 10%.",
    "card.thread_priority.desc":
      "alle Knoten, in Reichweite",
    "card.greed_protocol.desc":
      "+60% Kill-Credits / −20% globaler Schaden.",
    "card.minimalist.desc":
      "+15% globaler Schaden pro LEEREM Skill-Slot.",
    "card.full_refund.desc":
      "Verkaufte Knoten erstatten 100% der Investition.",
    "card.quantum_surge.desc":
      "",
    "card.hyper_threading.desc":
      "Jeder Knoten feuert 2 Wellen lang mit 2-facher Rate.",
    "card.root_access.desc":
      "Alle Feinde 1 Welle lang auf 40% Tempo gedrosselt.",
    "card.zero_day_exploit.desc":
      "+50% Schaden gegen BOSS- und GOLEM-Klasse für 2 Wellen.",
    "card.memory_overflow.desc":
      "Die Draft-Phase bietet dauerhaft +1 Karte.",
    "card.glass_protocol.desc":
      "+60% globaler Schaden / −2 maximale Integrität.",
    "card.scorched_earth.desc":
      "+30% globaler Schaden / Kill-Credits halbiert.",
    "card.resonance.desc":
      "Jede aktive Synergie gewährt +12% globalen Schaden.",
    "card.memory_leak.desc":
      "Stärkste Gegnerklasse: −40 % HP (nie tödlich), dann EXPONIERT +50 % Turmschaden.",
    "card.cascade.desc":
      "Jeder Synergie-Trigger verteilt 25 AoE-Schaden an nahe Feinde.",
    "card.late_overclock.desc":
      "Alle Knoten feuern dauerhaft mit +35% Rate.",
    "card.neural_nexus.desc":
      "Angrenzende 3-Element-Gruppen feuern alle 2s Auto-Kill-Schüsse.",
    "card.time_warp.desc":
      "Drücke [T]: 5s Realitäts-Freeze — deine Knoten feuern, die Feinde stehen still.",
    "card.recursive_loop.desc":
      "Kill detoniert Schrapnell: 25% der Maximal-HP des Opfers auf ALLE auf dem Schirm.",
    "card.apex_predator.desc":
      "Türme deines DOMINANTEN Elements +120% Schaden / alle anderen −40%.",
    // GA-09 — siehe EN-Seite.
    "card.monoculture.desc":
      "Schaltet RESONANZ für je 3+ benachbarte Türme frei, unabhängig vom Element / −25% globaler Schaden.",
    "card.kernel_panic.desc":
      "ALLE Bedrohungen verlieren 40% aktuelle HP (Bosse 15%).",
    "card.pid_hijack.desc":
      "Das Ziel kehrt um und verursacht 120 DPS an allem, was es passiert.",
    "card.gc_force.desc":
      "",
    "card.logic_bomb.desc":
      "Detoniert nach 2s — AoE skaliert mit der Cluster-Dichte.",
    "card.backdoor.desc":
      "Die 5 am weitesten vorgedrungenen Bedrohungen werden zum Spawn zurückgerissen.",
    "card.fork_bomb.desc":
      "verbreitet DoT + Verlangsamung im Schwarm",
    "card.entropy_tax.desc":
      "",
    "card.dead_mans_switch.desc":
      "Fällt die Integrität unter 3, verdoppelt sich ALLER Schaden bis zum Wellenende.",
    // GA-10 (Spec C+) — card.headline: eigenständige deutsche Kurzformeln, keine
    // Eins-zu-eins-Kopie des Englischen.
    "card.overload_malware.headline": "+20% FEUERSCHADEN",
    "card.overload_ice.headline": "+20% KRYO-SCHADEN",
    "card.overload_emp.headline": "+20% ENTLADUNGSSCHADEN",
    "card.firewall_boost.headline": "+1 INTEGRITÄT",
    "card.data_mining.headline": "+20% CREDITS JE KILL",
    "card.overclock.headline": "+20% FEUERRATE",
    "card.cache_hit.headline": "+15% REICHWEITE",
    "card.packet_burst.headline": "+75¢ SOFORT",
    "card.packet_inspector.headline": "ALLE HP-BALKEN SICHTBAR",
    "card.piercing_shots.headline": "+1 DURCHSCHLAG",
    "card.throttle_field.headline": "−15% GEGNERTEMPO",
    "card.synergy_bridge.headline": "SYNERGIEN IN 8 RICHTUNGEN",
    "card.chain_reaction.headline": "1,5× SYNERGIE-RATE",
    "card.bounty_protocol.headline": "+40% CREDITS · 3 WELLEN",
    "card.emergency_patch.headline": "+3 INTEGRITÄT",
    "card.intrusion_scanner.headline": "SCANNT 3 WELLEN",
    "card.firewall_reinforce.headline": "FIREWALLS −10% TEMPO",
    "card.thread_priority.headline": "ZIEL: SCHNELLSTE",
    "card.greed_protocol.headline": "+60% CREDITS / −20% SCHADEN",
    "card.minimalist.headline": "+15% SCHADEN / LEERER SLOT",
    "card.full_refund.headline": "100% VERKAUFSERSTATTUNG",
    "card.quantum_surge.headline": "2× GESAMTSCHADEN",
    "card.hyper_threading.headline": "2× FEUERRATE",
    "card.root_access.headline": "40% TEMPO",
    "card.zero_day_exploit.headline": "+50% GEGEN BOSS/GOLEM",
    "card.memory_overflow.headline": "+1 DRAFT-KARTE",
    "card.glass_protocol.headline": "+60% SCHADEN / −2 INTEGRITÄT",
    "card.scorched_earth.headline": "+30% SCHADEN / −50% CREDITS",
    "card.resonance.headline": "+12% SCHADEN / SYNERGIE",
    "card.memory_leak.headline": "−40% HP · EXPONIERT 5s",
    "card.cascade.headline": "SYNERGIE → 25 AOE-SCHADEN",
    "card.late_overclock.headline": "+35% FEUERRATE",
    "card.neural_nexus.headline": "DREIFACH-CLUSTER AUTO-KILL",
    "card.time_warp.headline": "[T] 5s FREEZE · 1/WELLE",
    "card.recursive_loop.headline": "10. KILL → 25% AOE",
    "card.apex_predator.headline": "+120% HAUPT / −40% ANDERE",
    "card.monoculture.headline": "RESONANZ ÜBERALL / −25% SCHADEN",
    "card.kernel_panic.headline": "40% HP-NUKE · BOSS 15%",
    "card.pid_hijack.headline": "ÜBERNAHME · AMOKLAUF · ◎ ZIELEN",
    "card.gc_force.headline": "PURGE <25% HP · +5¢/KILL",
    "card.logic_bomb.headline": "VERZÖGERTE AOE · ◎ ZIELEN",
    "card.backdoor.headline": "REISST 5 ZURÜCK → SPAWN",
    "card.fork_bomb.headline": "KONTAGION · ◎ ZIELEN",
    "card.entropy_tax.headline": "+25% GESAMTSCHADEN",
    "card.dead_mans_switch.headline": "2× SCHADEN BEI <3 LEBEN",
    // Spawn alert (CR2)
    "spawn.alert.line1": "WARNUNG: Neuer Bedrohungsvektor erkannt.",
    "spawn.alert.line2": "Netzwerk-Leck imminent.",
    "spawn.alert.dismiss": "BESTÄTIGT",
    // Sell dialog
    "sell.invested": "INVESTIERT",
    "sell.refund": "ERSTATTUNG",
    "sell.pathWarning": "PFAD WIRD NEU BERECHNET",
    "sell.confirm": "VERKAUF BESTÄTIGEN",
    "sell.cancel": "ABBRECHEN",
    "sell.towerTitle": "TURM ABBAUEN",
    "sell.firewallTitle": "FIREWALL ABBAUEN",
    // Early-game threat diagnostics (P23)
    // S126 \u2014 die ECHELON-Korruptions-Akt-Hinweise (reine Render-Erz\u00E4hlung)
    // Task-004 (2026-threat-profile-model, AC-10): same five names as EN
    // (owner decision 2026-08-14: stay English in both locales, uppercase;
    // see the EN block above for the full rationale).
    "threatLevel.sandbox": "SANDBOX",
    "threatLevel.containment": "CONTAINMENT",
    "threatLevel.lockdown": "LOCKDOWN",
    "threatLevel.overrun": "OVERRUN",
    "threatLevel.fallout": "FALLOUT",
    // Task-004 (2026-prestart-deployment-redesign, AC-20/AC-21a): same two
    // templates as EN above (see the EN block for the full rationale).
    "threatLevel.lock.release": "NICHT FREIGEGEBEN",
    "threatLevel.lock.runs": "NOCH {{n}} EINSÄTZE",
  });

  // Comms / narrative corpus (BL-89) \u2014 VERA/FENRIR barks, commander quips, swap
  // advice, mission briefing + deploy lines. EN mirrors the canonical source
  // (the keyed arrays); DE is idiomatic, voice-preserving German. Registered
  // here so the `comms.*` family lands in the same EN/DE parity gate.
  registerTranslations("en", {
    ...COMMS_EN,
    ...COMMS_QUIP_EN,
    ...COMMS_BRIEF_EN,
    ...STORY_EN,
  });
  registerTranslations("de", {
    ...COMMS_DE,
    ...COMMS_QUIP_DE,
    ...COMMS_BRIEF_DE,
    ...STORY_DE,
  });
}
